/**
 * Page principale du jeu de Tarot
 * 
 * Utilise le système de queue d'actions pour un ordonnancement correct
 * des événements du jeu (cartes jouées, fin de pli, etc.)
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import Card from '../components/Card';
import PlayerHand from '../components/PlayerHand';
import TrickDisplay from '../components/TrickDisplay';
import type { CardNumber, GameTable, GamePlayer } from '../types/game';
import { Contract, ContractNames, SuitNames, Suit } from '../types/game';
import { getPlayableCards, getCardName, countPoints } from '../utils/cards';
import {
  hasAllKingsInHand,
  kingCardForSuit,
  queenCardForSuit,
  encodeKingCallAppelee,
  encodeQueenCallAppelee,
} from '../game/kingCall';
import { PlayerAvatar } from '../components/PlayerAvatar';
import ClickablePseudo from '../components/ClickablePseudo';
import LiveClassement, { type LivePlayerInfo, type ClassementSortBy, isLivePlayerFinished } from '../components/LiveClassement';
import DonneCounter from '../components/DonneCounter';
import SystemToastContainer, { type Toast } from '../components/SystemToast';
import { useGameQueue, GameState as QueueGameState, PoigneeType, PoigneeNames, getPoigneeThresholds, DELAYS } from '../game';
import type { DeclaredPoignee } from '../game/gameActions';
import type { GameHistory } from '../game/gameHistory';
import ReplayMode from '../components/ReplayMode';
import TimedReplayView from '../components/TimedReplayView';
import { useSpectatorPlayback } from '../hooks/useSpectatorPlayback';
import CardCountdown from '../components/CardCountdown';
import { log } from '../utils/logger';
import { formatTablePlayerName } from '../utils/seatLabels';
import { useApp } from '../contexts/AppContext';
import GameDrawer from '../components/GameDrawer';
import { useResponsive } from '../hooks/useResponsive';
import type { Session, GameConnectionInfo, TableBase, DuplicateTableStyle } from '../types/app';
import { useResolvedTheme } from '../hooks/useTheme';
import { setBetaPageDetail } from '../utils/betaFeedbackContext';
import '../styles/spectator.css';
import TableBackgroundLayer from '../components/TableBackgroundLayer';
import type { TrickEndEvent, ChienEvent, ScreenPosition } from '../components/TableBackgroundLayer';
import {
  neoStateToActions,
  neoDealToActions,
  neoBidToActions,
  neoKingCalledToActions,
  neoChienToActions,
  neoEcartDoneToActions,
  neoCardPlayedToActions,
  neoPoigneeDeclaredToActions,
  neoTrickEndToActions,
  neoScoresToActions,
  neoAllPassedToActions,
  type NeoStatePayload,
} from '../neo/neoAdapter';
import AddToLibraryModal from '../components/library/AddToLibraryModal';
import type { LibraryOriginType } from '../types/library';
import '../styles/game.css';
import '../styles/chat.css';
import '../styles/library.css';
import { BACKEND_URL } from '../config';
import { computeHasOtherHumansForDnd } from '../lib/gamePresence';

// Type local pour compatibilité avec l'ancien code
type GameState = {
  phase: number;
  tableId: string;
  idPartie?: string;
  players: GamePlayer[];
  myPosition: number;
  myHand: CardNumber[];
  currentTrick: CardNumber[];
  trickPlayerPositions: number[];
  playerCount: number;
  currentPlayer: number;
  dealer?: number;
  entameur?: number;
  taker?: number;
  currentContract?: Contract;
  calledKing?: CardNumber;
  partner?: number;
  partnerRevealed?: boolean;
  lastTrick?: CardNumber[];
  chien?: CardNumber[];
  numPli?: number;
  scores?: any;
};

// Session importé depuis types/app

interface GameProps {
  session: Session;
  gameInfo: GameConnectionInfo;
}

// ============================================
// Types pour le chat de table
// ============================================

interface TableChatMessage {
  id: string;
  pseudo: string;      // '' pour messages système
  text: string;
  timestamp: number;
  isSystem: boolean;
  isOwn: boolean;
}

// ============================================
// Composant pour afficher un joueur à la table
// ============================================

interface PlayerSeatProps {
  player?: GamePlayer;
  position: 'bottom' | 'left' | 'top' | 'right' | 'top-left' | 'top-right';
  isCurrentPlayer: boolean;
  isTaker: boolean;
  takerContract?: Contract;  // Contrat du preneur (pour affichage)
  isBidding: boolean;       // Phase d'enchères en cours
  isCurrentBidder: boolean; // C'est à ce joueur d'enchérir
  isTrickEnding: boolean;   // Le pli est complet, en attente de ramassage
  isPartnerRevealed?: boolean;
  isSelfCalled?: boolean;   // Preneur auto-appelé (roi au chien)
  cardCount?: number;
  tricksWon: number;
}

function PlayerSeat({ 
  player, 
  position, 
  isCurrentPlayer, 
  isTaker, 
  takerContract,
  isBidding,
  isCurrentBidder,
  isTrickEnding,
  isPartnerRevealed,
  isSelfCalled,
  cardCount: _cardCount = 0,
  tricksWon,
}: PlayerSeatProps) {
  void _cardCount; // Prévu pour usage futur
  if (!player) {
    return (
      <div className={`player-seat player-seat-${position} empty`}>
        <div className="player-info">
          <span className="player-name">En attente...</span>
        </div>
      </div>
    );
  }
  
  const seatClasses = [
    'player-seat',
    `player-seat-${position}`,
    isCurrentPlayer && !isTrickEnding && 'current-player',  // Pas de halo en fin de pli
    isTaker && 'is-taker',
    isCurrentBidder && 'current-bidder',
  ].filter(Boolean).join(' ');
  
  return (
    <div className={seatClasses}>
      <div className="player-info">
        <ClickablePseudo pseudo={player.pseudo} className="player-name">{player.pseudo}</ClickablePseudo>
        {player.elo && <span className="player-elo">{player.elo}</span>}
        <div className="player-badges">
          {isTaker && !isBidding && takerContract !== undefined && (
            <span className="taker-badge">{ContractNames[takerContract]}</span>
          )}
          {isPartnerRevealed && isSelfCalled && (
            <span className="partner-badge self-called">auto-appelé</span>
          )}
          {isPartnerRevealed && !isSelfCalled && (
            <span className="partner-badge">Appelé</span>
          )}
          {isCurrentPlayer && !isBidding && !isTrickEnding && <span className="turn-indicator" title="À ce joueur de jouer">▶</span>}
          {isCurrentBidder && <span className="bidder-indicator" title="À lui d'enchérir">🎯</span>}
        </div>
        {/* Afficher l'enchère pendant la phase d'enchères */}
        {isBidding && player.bid !== undefined && (
          <span className={`player-bid ${player.bid === 0 ? 'passed' : 'active'}`}>
            {ContractNames[player.bid]}
          </span>
        )}
      </div>
      
      {tricksWon > 0 && (
        <div className="tricks-won">
          <span>{tricksWon} pli{tricksWon > 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// Minuteur de prochain tournoi
// ============================================

interface CountdownTimerProps {
  targetTime: number;
  label: string;
  expiredLabel: string;
  approximate?: boolean;
  onExpired?: () => void;
}

function CountdownTimer({ targetTime, label, expiredLabel, approximate, onExpired }: CountdownTimerProps) {
  const [remaining, setRemaining] = useState(Math.max(0, Math.ceil((targetTime - Date.now()) / 1000)));
  
  useEffect(() => {
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil((targetTime - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0 && onExpired) {
        // Ne pas appeler onExpired immédiatement : laisser le message "va bientôt débuter" visible
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [targetTime, onExpired]);
  
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const expired = remaining <= 0;
  
  return (
    <div className={`countdown-timer ${expired ? 'expired' : ''}`}>
      <span className="countdown-icon">&#9201;</span>
      {expired ? (
        <span className="countdown-waiting">{expiredLabel}</span>
      ) : (
        <>
          <span className="countdown-text">
            {label}{approximate ? ' ≈' : ''}
          </span>
          <span className="countdown-value">
            {minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`}
          </span>
        </>
      )}
    </div>
  );
}

// ============================================
// Panneau d'enchères (affiché au centre du tapis)
// ============================================

interface BiddingPanelProps {
  currentContract: Contract;
  onBid: (contract: Contract) => void;
  myTurn: boolean;
  currentBidderName?: string;
  bidConstraints?: { min: number; max: number };  // Contraintes de contratObligatoire
}

function BiddingPanel({ currentContract, onBid, myTurn, currentBidderName, bidConstraints }: BiddingPanelProps) {
  const contracts = [
    { value: 0, label: 'Passe', shortLabel: '⊘', minRequired: -1 },
    { value: 1, label: 'Petite', shortLabel: 'P', minRequired: 0 },
    { value: 2, label: 'Garde', shortLabel: 'G', minRequired: 1 },
    { value: 3, label: 'Garde Sans', shortLabel: 'GS', minRequired: 2 },
    { value: 4, label: 'Garde Contre', shortLabel: 'GC', minRequired: 3 },
  ];
  
  // Appliquer les contraintes de contratObligatoire
  const isAllowed = (value: number) => {
    if (!bidConstraints) {
      return value === 0 || currentContract < value;
    }
    if (value < bidConstraints.min || value > bidConstraints.max) return false;
    if (value !== 0 && currentContract >= value) return false;
    return true;
  };
  
  // Vérifier si le joueur est forcé de passer (min=0, max=0)
  const forcedToPass = bidConstraints && bidConstraints.min === 0 && bidConstraints.max === 0;
  
  return (
    <div className={`bidding-overlay ${myTurn ? 'my-turn' : ''}`}>
      <div className="bidding-overlay-inner">
        <div className="bidding-overlay-header">
          <span className="bidding-overlay-title">Enchères</span>
          {currentContract > 0 && (
            <span className="bidding-overlay-contract">
              {ContractNames[currentContract]}
            </span>
          )}
        </div>
        
        {myTurn ? (
          <>
            {forcedToPass ? (
              <p className="bidding-overlay-prompt forced-pass">Vous devez passer</p>
            ) : (
              <p className="bidding-overlay-prompt">À vous d'enchérir</p>
            )}
            <div className="bidding-overlay-buttons">
              {contracts.map(({ value, label }) => {
                const allowed = isAllowed(value);
                return (
                  <button
                    key={value}
                    className={`bidding-overlay-btn ${value === 0 ? 'pass' : 'bid'} ${value === currentContract + 1 ? 'suggested' : ''}`}
                    disabled={!allowed}
                    onClick={() => onBid(value as Contract)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <p className="bidding-overlay-waiting">
            En attente de <strong>{currentBidderName || '...'}</strong>
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================
// Panneau d'appel du roi (5 joueurs)
// ============================================

const KING_CALL_SUITS = [
  { index: 0, label: SuitNames[Suit.Hearts] },
  { index: 1, label: SuitNames[Suit.Spades] },
  { index: 2, label: SuitNames[Suit.Diamonds] },
  { index: 3, label: SuitNames[Suit.Clubs] },
];

interface KingCallPanelProps {
  myTurn: boolean;
  canCallQueen: boolean;
  onCallKing: (suitIndex: number) => void;
  onCallQueen: (suitIndex: number) => void;
}

function KingCallPanel({ myTurn, canCallQueen, onCallKing, onCallQueen }: KingCallPanelProps) {
  const [mode, setMode] = useState<'king' | 'queen'>(canCallQueen ? 'queen' : 'king');

  return (
    <div className={`king-call-overlay ${myTurn ? 'my-turn' : ''}`}>
      <div className="king-call-overlay-inner">
        <div className="king-call-overlay-header">
          <span className="king-call-overlay-title">Appel du roi</span>
        </div>

        {myTurn ? (
          <>
            <p className="king-call-overlay-prompt">
              {mode === 'queen'
                ? 'Vous avez les 4 rois — appelez une dame'
                : 'Choisissez le roi à appeler'}
            </p>
            {canCallQueen && mode === 'king' && (
              <button
                type="button"
                className="king-call-switch-btn"
                onClick={() => setMode('queen')}
              >
                Appeler une dame (4 rois en main)
              </button>
            )}
            {canCallQueen && mode === 'queen' && (
              <button
                type="button"
                className="king-call-switch-btn"
                onClick={() => setMode('king')}
              >
                Appeler un roi
              </button>
            )}
            <div className="king-call-suit-grid">
              {KING_CALL_SUITS.map(({ index, label }) => {
                const card = mode === 'queen' ? queenCardForSuit(index) : kingCardForSuit(index);
                return (
                  <button
                    key={index}
                    type="button"
                    className="king-call-suit-btn"
                    onClick={() => (mode === 'queen' ? onCallQueen(index) : onCallKing(index))}
                    title={getCardName(card)}
                  >
                    <Card card={card} size="small" />
                    <span className="king-call-suit-label">{label}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <p className="king-call-overlay-waiting">
            Le preneur choisit son partenaire…
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================
// Révélation du roi appelé (défenseurs, 5 joueurs)
// ============================================

interface CalledKingRevealProps {
  calledKing: CardNumber;
  onDismiss: () => void;
}

function CalledKingReveal({ calledKing, onDismiss }: CalledKingRevealProps) {
  const isQueen = getCardName(calledKing).includes('Dame');

  return (
    <button
      type="button"
      className="called-king-reveal-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onDismiss();
      }}
      aria-label="Fermer l'affichage du roi appelé"
    >
      <div className="called-king-reveal-inner">
        <Card card={calledKing} size="large" />
        <span className="called-king-label">
          {isQueen ? 'Dame appelée' : 'Roi appelé'}
        </span>
        <span className="called-king-reveal-hint">Cliquez ou attendez…</span>
      </div>
    </button>
  );
}

// ============================================
// Tableau de suivi des enchères (sidebar)
// ============================================

interface BidTrackerProps {
  players: GamePlayer[];
  dealer: number;
  currentPlayer: number;
  currentContract: Contract;
  taker?: number;
  playerCount: number;
}

/** Colonnes d'enchères */
const BID_COLUMNS = [
  { value: 0, label: '⊘', title: 'Passe' },
  { value: 1, label: 'P', title: 'Petite' },
  { value: 2, label: 'G', title: 'Garde' },
  { value: 3, label: 'GS', title: 'Garde Sans' },
  { value: 4, label: 'GC', title: 'Garde Contre' },
];

function BidTracker({ players, dealer, currentPlayer, currentContract, taker, playerCount }: BidTrackerProps) {
  // Calculer l'ordre des enchères : le joueur après le donneur commence
  const biddingOrder: number[] = [];
  for (let i = 1; i <= playerCount; i++) {
    biddingOrder.push((dealer + i) % playerCount);
  }
  
  // Ne montrer que les joueurs ayant déjà enchéri + celui en cours
  const visiblePlayers = biddingOrder.filter(playerIdx => {
    const player = players[playerIdx];
    if (!player) return false;
    // Déjà enchéri
    if (player.bid !== undefined) return true;
    // En train d'enchérir
    if (playerIdx === currentPlayer) return true;
    return false;
  });
  
  return (
    <div className="bid-tracker">
      <div className="bid-tracker-header">
        <span className="bid-tracker-title">Suivi des enchères</span>
      </div>
      <table className="bid-tracker-table">
        <colgroup>
          <col className="player-col" />
          {BID_COLUMNS.map(col => (
            <col key={col.value} className="bid-col" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="bid-tracker-col-player">Joueur</th>
            {BID_COLUMNS.map(col => (
              <th key={col.value} className="bid-tracker-col-bid" title={col.title}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visiblePlayers.map(playerIdx => {
            const player = players[playerIdx];
            if (!player) return null;
            
            const hasBid = player.bid !== undefined;
            const isCurrentBidder = playerIdx === currentPlayer && !hasBid;
            const isTaker = playerIdx === taker && currentContract > 0;
            
            return (
              <tr
                key={playerIdx}
                className={[
                  'bid-tracker-row',
                  isCurrentBidder && 'is-bidding',
                  isTaker && 'is-taker',
                  hasBid && 'has-bid',
                ].filter(Boolean).join(' ')}
              >
                <td className="bid-tracker-col-player">
                  <div className="bid-tracker-player-info">
                    <PlayerAvatar pseudo={player.pseudo} size={22} />
                    <ClickablePseudo pseudo={player.pseudo} className="bid-tracker-pseudo">{player.pseudo}</ClickablePseudo>
                    {isCurrentBidder && <span className="bid-tracker-thinking">…</span>}
                  </div>
                </td>
                {BID_COLUMNS.map(col => (
                  <td
                    key={col.value}
                    className={[
                      'bid-tracker-col-bid',
                      hasBid && player.bid === col.value && 'is-selected',
                      hasBid && player.bid === col.value && `bid-value-${col.value}`,
                      hasBid && player.bid === col.value && col.value > 0 && col.value === currentContract && 'is-best',
                    ].filter(Boolean).join(' ')}
                  >
                    {hasBid && player.bid === col.value && (
                      <span className="bid-tracker-mark">●</span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Panneau du chien (Phase 4 - Visualisation)
// ============================================

interface ChienViewProps {
  chien: CardNumber[];
  allPassed: boolean;  // Tout le monde a passé
  onClick: () => void;
}

function ChienView({ chien, allPassed, onClick }: ChienViewProps) {
  const chienArray = Array.isArray(chien) ? chien : [];
  
  if (chienArray.length === 0) return null;
  
  return (
    <div className="chien-area" onClick={onClick}>
      <div className="chien-label">
        {allPassed ? "Tout le monde a passé" : "Le Chien"}
      </div>
      <div className="chien-cards">
        {chienArray.map((card, index) => (
          <div key={`chien-${card}-${index}`} className="chien-card">
            <Card card={card} size="medium" />
          </div>
        ))}
      </div>
      <div className="chien-hint">Cliquez pour continuer</div>
    </div>
  );
}

// ============================================
// Panneau d'écart (Phase 5)
// ============================================

// Fonction pour vérifier si une carte peut être écartée
function canDiscard(card: CardNumber): boolean {
  // Pas d'atouts (couleur 4 = atouts, cartes 56-77 + excuse 78)
  if (card >= 56 && card <= 78) return false;
  
  // Pas de Rois (hauteur 14 = Roi)
  // Coeur: 14, Pique: 28, Carreau: 42, Trèfle: 56
  if (card === 14 || card === 28 || card === 42 || card === 56) return false;
  
  return true;
}

/** Nombre de cartes à écarter (chien vidé en phase 5 → ne pas se fier à chien.length) */
function ecartRequiredCount(playerCount: number, chienLength?: number): number {
  if (playerCount === 5) return 3;
  return chienLength ?? 6;
}

function readElementRotation(el: Element): number {
  const t = window.getComputedStyle(el).transform;
  if (!t || t === 'none') return 0;
  const m2 = t.match(/matrix\(([^)]+)\)/);
  if (m2) {
    const v = m2[1].split(',').map(Number);
    return Math.round((Math.atan2(v[1], v[0]) * 180) / Math.PI * 10) / 10;
  }
  const m3 = t.match(/matrix3d\(([^)]+)\)/);
  if (m3) {
    const v = m3[1].split(',').map(Number);
    return Math.round((Math.atan2(v[1], v[0]) * 180) / Math.PI * 10) / 10;
  }
  return 0;
}

function captureCardRect(card: CardNumber, root: string): { rect: DOMRect; rotation: number } | null {
  try {
    const cardName = getCardName(card);
    const cardEl = document.querySelector(`${root} .card[title="${CSS.escape(cardName)}"]`);
    if (!cardEl) return null;
    const wrapper = cardEl.closest('.hand-card-wrapper, .ecart-card') ?? cardEl;
    return { rect: cardEl.getBoundingClientRect(), rotation: readElementRotation(wrapper) };
  } catch {
    return null;
  }
}

const ECART_FLY_MS = 450;

function playEcartFlip(
  el: HTMLElement,
  origin: { rect: DOMRect; rotation: number },
  animateRotate: boolean,
  measureEl?: HTMLElement | null,
): void {
  const last = (measureEl ?? el).getBoundingClientRect();
  const dx = (origin.rect.left + origin.rect.width / 2) - (last.left + last.width / 2);
  const dy = (origin.rect.top + origin.rect.height / 2) - (last.top + last.height / 2);
  const sx = origin.rect.width / Math.max(1, last.width);
  el.style.translate = `${dx}px ${dy}px`;
  el.style.scale = `${sx}`;
  if (animateRotate) el.style.rotate = `${origin.rotation}deg`;
  void el.offsetWidth;
  el.style.transition =
    `translate ${ECART_FLY_MS}ms cubic-bezier(0.16, 1, 0.3, 1), ` +
    `rotate ${ECART_FLY_MS}ms cubic-bezier(0.16, 1, 0.3, 1), ` +
    `scale ${ECART_FLY_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`;
  el.style.translate = '0 0';
  el.style.scale = '1';
  if (animateRotate) el.style.rotate = '0deg';
  window.setTimeout(() => {
    el.style.translate = '';
    el.style.scale = '';
    el.style.transition = '';
    if (animateRotate) el.style.rotate = '';
  }, ECART_FLY_MS + 40);
}

interface EcartPanelProps {
  selectedCards: CardNumber[];
  requiredCount: number;
  onValidate: () => void;
  onRemoveCard: (card: CardNumber) => void;
  onClearAll: () => void;
}

function EcartPanel({ selectedCards, requiredCount, onValidate, onRemoveCard, onClearAll }: EcartPanelProps) {
  const isComplete = selectedCards.length === requiredCount;
  const points = countPoints(selectedCards);
  
  return (
    <div className="ecart-area ecart-area-with-cards">
      <div className="ecart-label">
        <span>Écart {selectedCards.length}/{requiredCount}</span>
        {selectedCards.length > 0 && (
          <span className="ecart-points">{points} pts</span>
        )}
      </div>
      <div className="ecart-cards">
        {selectedCards.length === 0 ? (
          <span className="ecart-cards-empty">
            Levez ou touchez une carte de votre main
          </span>
        ) : (
          selectedCards.map((card) => (
            <div 
              key={`ecart-${card}`}
              data-ecart-card={card}
              className="ecart-card clickable"
              onClick={() => onRemoveCard(card)}
              title="Remettre dans la main"
            >
              <Card card={card} size="small" />
            </div>
          ))
        )}
      </div>
      {selectedCards.length > 0 && (
        <div className="ecart-hint">Touchez une carte ici ou le fantôme en main pour la retirer</div>
      )}
      <div className="ecart-buttons">
        <button 
          className="ecart-button validate"
          disabled={!isComplete}
          onClick={onValidate}
        >
          Valider l'écart
        </button>
        {selectedCards.length > 0 && (
          <button className="ecart-button cancel" onClick={onClearAll}>
            Annuler
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================
// Fonctions utilitaires pour la poignée
// ============================================

/**
 * Compte le nombre d'atouts dans une main (incluant l'excuse si demandé)
 */
function countTrumpsInHand(hand: CardNumber[], includeExcuse: boolean = true): number {
  let count = 0;
  for (const card of hand) {
    // Atouts: cartes 57-77 (atout 1 à atout 21)
    if (card >= 57 && card <= 77) count++;
    // Excuse: carte 78
    if (includeExcuse && card === 78) count++;
  }
  return count;
}

/**
 * Trie les cartes d'une poignée selon la règle officielle :
 * du plus haut atout au plus faible, l'excuse en dernier si présente.
 * (Atout 21 = carte 77 en premier, Atout 1 / Petit = carte 57 en dernier, Excuse = 78 tout à la fin)
 */
function sortPoigneeCards(cards: CardNumber[]): CardNumber[] {
  return [...cards].sort((a, b) => {
    // L'excuse (78) va toujours en dernier
    if (a === 78) return 1;
    if (b === 78) return -1;
    // Les atouts sont triés par valeur décroissante (77 → 57)
    return b - a;
  });
}

/**
 * Détermine le type de poignée le plus élevé déclarable avec une main donnée.
 * Les seuils dépendent du nombre de joueurs :
 * - 3 joueurs : Simple = 13, Double = 15, Triple = 18
 * - 4 joueurs : Simple = 10, Double = 13, Triple = 15
 * - 5 joueurs : Simple =  8, Double = 10, Triple = 13
 */
function getMaxPoigneeType(hand: CardNumber[], playerCount: number): PoigneeType {
  const thresholds = getPoigneeThresholds(playerCount);
  const nbAtouts = countTrumpsInHand(hand, true);
  if (nbAtouts >= thresholds[PoigneeType.Triple]) return PoigneeType.Triple;
  if (nbAtouts >= thresholds[PoigneeType.Double]) return PoigneeType.Double;
  if (nbAtouts >= thresholds[PoigneeType.Simple]) return PoigneeType.Simple;
  return PoigneeType.None;
}

/**
 * Détermine le type de poignée correspondant à un nombre de cartes sélectionnées.
 * Le nombre de cartes doit correspondre exactement au seuil.
 */
function getPoigneeTypeForCount(count: number, playerCount: number): PoigneeType {
  const thresholds = getPoigneeThresholds(playerCount);
  if (count === thresholds[PoigneeType.Triple]) return PoigneeType.Triple;
  if (count === thresholds[PoigneeType.Double]) return PoigneeType.Double;
  if (count === thresholds[PoigneeType.Simple]) return PoigneeType.Simple;
  return PoigneeType.None;
}

/**
 * Vérifie si une carte peut être ajoutée à la poignée.
 * - Atouts (57-77) : toujours OK
 * - Excuse (78) : seulement si le joueur n'a pas assez d'atouts purs pour compléter
 *   une poignée. « L'Excuse dans la poignée indique que l'annonceur n'a pas d'autre Atout. »
 *   Concrètement : l'excuse est autorisée si le nombre d'atouts purs (sans excuse)
 *   correspond exactement à un seuil de poignée - 1.
 * - Autres cartes : jamais
 */
function canAddToPoignee(card: CardNumber, hand: CardNumber[], playerCount: number): boolean {
  if (card === 78) {
    // L'excuse ne peut être ajoutée que pour compléter une poignée
    // = quand le nombre d'atouts purs est exactement un seuil - 1
    const thresholds = getPoigneeThresholds(playerCount);
    const trumpsWithoutExcuse = countTrumpsInHand(hand, false);
    return (
      trumpsWithoutExcuse === thresholds[PoigneeType.Simple] - 1 ||
      trumpsWithoutExcuse === thresholds[PoigneeType.Double] - 1 ||
      trumpsWithoutExcuse === thresholds[PoigneeType.Triple] - 1
    );
  }
  // Seuls les atouts (57-77) sont autorisés
  return card >= 57 && card <= 77;
}

/**
 * Auto-remplit la poignée avec les atouts de la main,
 * en ciblant la poignée la plus grande déclarable.
 * Les cartes sont triées du plus haut atout au plus faible (excuse en dernier).
 */
function autoFillPoignee(hand: CardNumber[], playerCount: number): CardNumber[] {
  const maxType = getMaxPoigneeType(hand, playerCount);
  if (maxType === PoigneeType.None) return [];
  
  const thresholds = getPoigneeThresholds(playerCount);
  const targetCount = thresholds[maxType];
  const result: CardNumber[] = [];
  
  // D'abord ajouter les vrais atouts (57-77), triés par valeur décroissante
  const trumps = hand
    .filter(c => c >= 57 && c <= 77)
    .sort((a, b) => b - a);
  
  for (const trump of trumps) {
    if (result.length >= targetCount) break;
    result.push(trump);
  }
  
  // Si on n'a pas assez, compléter avec l'excuse si elle est dans la main
  if (result.length < targetCount && hand.includes(78)) {
    result.push(78);
  }
  
  return result;
}

// ============================================
// Panneau de déclaration de poignée
// ============================================

interface PoigneePanelProps {
  selectedCards: CardNumber[];
  maxType: PoigneeType;
  playerCount: number;
  onValidate: () => void;
  onCancel: () => void;
  onRemoveCard: (card: CardNumber) => void;
}

function PoigneePanel({ selectedCards, maxType, playerCount, onValidate, onCancel, onRemoveCard }: PoigneePanelProps) {
  const currentType = getPoigneeTypeForCount(selectedCards.length, playerCount);
  const isValid = currentType !== PoigneeType.None;
  const thresholds = getPoigneeThresholds(playerCount);
  
  // Afficher les cartes triées du plus haut atout au plus faible
  const sortedSelected = sortPoigneeCards(selectedCards);
  
  // Liste des seuils possibles pour afficher dans le message d'aide
  const thresholdList = [thresholds[PoigneeType.Simple], thresholds[PoigneeType.Double], thresholds[PoigneeType.Triple]]
    .filter(t => t > 0)
    .join(', ');
  
  return (
    <div className="poignee-area">
      <div className="poignee-label">
        Déclarer une poignée ? ({PoigneeNames[maxType]} possible)
      </div>
      <div className="poignee-sublabel">
        {isValid 
          ? `${PoigneeNames[currentType]} — ${selectedCards.length} atouts`
          : `${selectedCards.length} atouts sélectionnés (besoin de ${thresholdList})`
        }
      </div>
      <div className="poignee-cards">
        {sortedSelected.length === 0 ? (
          <span style={{ color: 'rgba(255,255,255,0.5)', padding: '1rem' }}>
            Sélectionnez les atouts à déclarer
          </span>
        ) : (
          sortedSelected.map((card, index) => (
            <div 
              key={`poignee-${card}-${index}`} 
              className="poignee-card clickable"
              onClick={() => onRemoveCard(card)}
              title="Cliquez pour retirer de la poignée"
            >
              <Card card={card} size="small" />
            </div>
          ))
        )}
      </div>
      {sortedSelected.length > 0 && (
        <div className="poignee-hint">Cliquez sur une carte pour la retirer de la poignée</div>
      )}
      <div className="poignee-buttons">
        <button 
          className="poignee-button validate"
          disabled={!isValid}
          onClick={onValidate}
        >
          Déclarer la {isValid ? PoigneeNames[currentType].toLowerCase() : 'poignée'}
        </button>
        <button className="poignee-button cancel" onClick={onCancel}>
          Pas de poignée
        </button>
      </div>
    </div>
  );
}

// ============================================
// Affichage d'une poignée déclarée (par un autre joueur ou soi-même)
// ============================================

interface PoigneeDisplayProps {
  declaredPoignee: DeclaredPoignee;
  playerName: string;
}

function PoigneeDisplay({ declaredPoignee, playerName }: PoigneeDisplayProps) {
  // Tri officiel : du plus haut atout au plus faible, excuse en dernier
  const sortedCards = sortPoigneeCards(declaredPoignee.cards);
  
  return (
    <div className="poignee-display">
      <div className="poignee-display-header">
        <span className="poignee-display-player">{playerName}</span>
        <span className="poignee-display-type">{PoigneeNames[declaredPoignee.type]}</span>
      </div>
      <div className="poignee-display-cards">
        {sortedCards.map((card, index) => (
          <div key={`poignee-show-${card}-${index}`} className="poignee-display-card">
            <Card card={card} size="small" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Panneau de score
// ============================================

// Noms des points requis selon le nombre de bouts
const POINTS_REQUIS: Record<number, number> = {
  0: 56,
  1: 51,
  2: 41,
  3: 36,
};

interface ScorePanelProps {
  score: {
    // Données du serveur Java
    contrat?: number;
    contratRealise?: string | boolean;
    pointsJoueur?: number;
    difference?: number;
    nbBouts?: number;
    pointsAttaque?: number;
    scores?: number[];
  };
  players: GamePlayer[];
  onReady: () => void;
  isReady: boolean;
  isLive?: boolean;
  takerIndex?: number;
  hideReadyButton?: boolean;
  matchFinished?: boolean;
  onAddToLibrary?: () => void;
  readyButtonLabel?: string;
  readyDoneLabel?: string;
}

function ScorePanel({ score, players, onReady, isReady, isLive, takerIndex, hideReadyButton, matchFinished, onAddToLibrary, readyButtonLabel, readyDoneLabel }: ScorePanelProps) {
  // Déterminer si tout le monde a passé (pas de vrai score)
  const allPassed = score.contrat === undefined && score.pointsAttaque === 0 && score.nbBouts === 0;
  
  // Déterminer si le contrat est réussi
  const contratRealise = score.contratRealise === 'true' || score.contratRealise === true;
  const nbBouts = score.nbBouts ?? 0;
  const pointsRequis = POINTS_REQUIS[nbBouts] ?? 56;
  const pointsAttaque = score.pointsAttaque ?? score.pointsJoueur ?? 0;
  const difference = score.difference ?? (pointsAttaque - pointsRequis);
  
  return (
    <div className="score-panel">
      <h3>{allPassed ? 'Fin de la donne' : 'Résultat de la donne'}</h3>
      
      {allPassed ? (
        <div className="score-result all-passed">
          Tout le monde a passé !
        </div>
      ) : (
        <>
          {/* Taker info */}
          {takerIndex !== undefined && players[takerIndex] && (
            <div className="score-taker-info">
              <PlayerAvatar pseudo={players[takerIndex].pseudo} size={36} />
              <ClickablePseudo pseudo={players[takerIndex].pseudo} className="score-taker-name">{players[takerIndex].pseudo}</ClickablePseudo>
            </div>
          )}
          
          <div className={`score-result ${contratRealise ? 'won' : 'lost'}`}>
            {contratRealise ? 'Le preneur gagne !' : 'La défense gagne !'}
          </div>
          
          <div className="score-details">
            <div className="score-row">
              <span>Contrat:</span>
              <span>{score.contrat !== undefined ? ContractNames[score.contrat as Contract] : '-'}</span>
            </div>
            <div className="score-row">
              <span>Points attaque:</span>
              <span>{pointsAttaque}</span>
            </div>
            <div className="score-row">
              <span>Bouts:</span>
              <span>{nbBouts}</span>
            </div>
            <div className="score-row">
              <span>Points requis:</span>
              <span>{pointsRequis}</span>
            </div>
            <div className="score-row">
              <span>Différence:</span>
              <span className={difference >= 0 ? 'positive' : 'negative'}>
                {difference >= 0 ? '+' : ''}{difference}
              </span>
            </div>
          </div>
          
          {score.scores && score.scores.length > 0 && (
            <div className="player-scores">
              <h4>Scores</h4>
              {players.map((player, index) => {
                const playerScore = score.scores?.[index] ?? 0;
                return (
                  <div key={player.pseudo} className="player-score-row">
                    <ClickablePseudo pseudo={player.pseudo}>{player.pseudo}</ClickablePseudo>
                    <span className={playerScore >= 0 ? 'positive' : 'negative'}>
                      {playerScore >= 0 ? '+' : ''}{playerScore}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
      
      {onAddToLibrary && (
        <button className="library-basket-btn" onClick={onAddToLibrary}>
          📚 Ajouter à la bibliothèque
        </button>
      )}

      {!hideReadyButton && !matchFinished && (
        isLive ? (
          <div className="ready-auto">
            La prochaine donne va démarrer automatiquement…
          </div>
        ) : (
          <button 
            className={`ready-button ${isReady ? 'ready' : ''}`}
            onClick={onReady}
            disabled={isReady}
          >
            {isReady
              ? (readyDoneLabel ?? 'En attente des autres...')
              : (readyButtonLabel ?? 'Prêt pour la suite')}
          </button>
        )
      )}
      {matchFinished && (
        <div className="ready-auto">Partie terminée</div>
      )}
    </div>
  );
}

// ============================================
// Décompte de temps limite (toast sur le tapis de jeu)
// ============================================

/** Labels pour chaque type de phase */
const TIMER_LABELS: Record<string, string> = {
  contrat: 'Enchère',
  appel: 'Appel',
  ecart: 'Écart',
  carte: 'Carte',
  score: 'Prêt',
  donne: 'Donne',
};

function formatTimeoutRemaining(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${minutes}:${rest.toString().padStart(2, '0')}`;
  }
  return `${seconds}s`;
}

interface TimeoutCountdownProps {
  /** Temps total imparti (en secondes) */
  totalSeconds: number;
  /** Timestamp (ms) du début du décompte */
  startedAt: number;
  /** Type de phase ('contrat', 'appel', 'ecart', 'carte', 'score') */
  phaseType: string;
  /** Si true, ne pas afficher la notification initiale (3 premières secondes) */
  skipInitialNotification?: boolean;
}

/**
 * Composant affichant le décompte de temps restant pour agir.
 * 
 * Règles de visibilité (non intrusif) :
 * 1. Les 3 premières secondes après apparition à l'écran : affiché brièvement
 *    — sauf si skipInitialNotification est true (ex: plis suivants en phase carte)
 * 2. À la moitié du temps imparti : affiché pendant 5 secondes (rappel)
 * 3. Quand il reste ≤ 10 secondes : affiché en continu (urgent)
 * 
 * Note : startedAt est basé sur la réception du message serveur (avant les
 * animations de la queue). Le temps restant réel (pour mi-temps/urgent) est
 * calculé à partir de startedAt. Mais la notification initiale est basée sur
 * le moment d'apparition du composant, car des délais d'animation (chien, fin
 * de pli) peuvent séparer startedAt de l'affichage effectif.
 */
function TimeoutCountdown({ totalSeconds, startedAt, phaseType, skipInitialNotification }: TimeoutCountdownProps) {
  // Initialiser remaining avec la vraie valeur (évite un flash incorrect au 1er rendu)
  const [remaining, setRemaining] = useState(() => {
    const elapsed = (Date.now() - startedAt) / 1000;
    return Math.max(0, Math.ceil(totalSeconds - elapsed));
  });
  
  // Timestamp d'apparition à l'écran (peut différer de startedAt à cause des
  // délais d'animation : 8s chien, 2s fin de pli, etc.)
  // Grâce au key={...} sur le composant, useState est réinitialisé à chaque nouveau tour.
  const [appearedAt] = useState(() => Date.now());
  
  useEffect(() => {
    const update = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setRemaining(Math.max(0, Math.ceil(totalSeconds - elapsed)));
    };
    const interval = setInterval(update, 500);
    return () => clearInterval(interval);
  }, [totalSeconds, startedAt]);
  
  if (totalSeconds <= 0) return null;
  
  const halfTime = Math.floor(totalSeconds / 2);
  
  // L'annonce initiale est basée sur le temps depuis l'apparition à l'écran,
  // pas sur startedAt (qui peut être très antérieur à cause des animations)
  const timeSinceAppeared = (Date.now() - appearedAt) / 1000;
  const isInitial = !skipInitialNotification && timeSinceAppeared < 5;
  
  // Les annonces mi-temps et urgente sont basées sur le temps restant réel
  // (calculé depuis startedAt = moment de réception du message serveur)
  const isHalfway = remaining <= halfTime && remaining > halfTime - 5;
  const isUrgent = remaining <= 10;
  const visible = isInitial || isHalfway || isUrgent;
  
  // Déterminer le mode visuel
  const mode = isUrgent ? 'urgent' : isHalfway ? 'warning' : 'initial';
  const label = TIMER_LABELS[phaseType] || '';
  
  return (
    <div
      className={`timeout-countdown ${mode} ${visible ? 'visible' : ''}${phaseType === 'donne' ? ' timeout-countdown--donne' : ''}`}
      role="timer"
      aria-label={`${label}: ${remaining} secondes restantes`}
    >
      <span className="timeout-icon" aria-hidden="true">⏱</span>
      <span className="timeout-label">{label}</span>
      <span className="timeout-value">{formatTimeoutRemaining(remaining)}</span>
    </div>
  );
}

// ============================================
// Helper pour grouper les messages système "encadré"
// (séparés par des lignes de tirets)
// ============================================

interface ChatRenderItem {
  type: 'message' | 'encadre' | 'tournament-results';
  messages: TableChatMessage[];   // Un seul élément pour 'message', plusieurs pour 'encadre'/'tournament-results'
  key: string;
}

/** Supprime le préfixe "- " d'un message système encadré */
function stripDashPrefix(text: string): string {
  return text.replace(/^-\s/, '');
}

function groupChatMessages(messages: TableChatMessage[]): ChatRenderItem[] {
  const result: ChatRenderItem[] = [];
  const isDashLine = (text: string) => /^-{3,}$/.test(text.trim());
  
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i];
    
    // Détecter le début d'un encadré (ligne de tirets système)
    if (msg.isSystem && isDashLine(msg.text)) {
      // Chercher la fin de l'encadré
      const blockMessages: TableChatMessage[] = [];
      let j = i + 1;
      let foundClosing = false;
      
      while (j < messages.length) {
        if (messages[j].isSystem && isDashLine(messages[j].text)) {
          // Trouvé la ligne de tirets de fermeture
          foundClosing = true;
          j++; // Sauter la ligne de tirets de fermeture
          break;
        }
        if (!messages[j].isSystem) {
          // Message non-système, casser le bloc
          break;
        }
        // Stocker le message avec le préfixe "- " supprimé
        blockMessages.push({
          ...messages[j],
          text: stripDashPrefix(messages[j].text),
        });
        j++;
      }
      
      if (foundClosing && blockMessages.length > 0) {
        result.push({
          type: 'encadre',
          messages: blockMessages,
          key: `encadre-${blockMessages[0].id}`,
        });
        i = j;
        continue;
      }
      // Pas un vrai encadré, ignorer la ligne de tirets isolée
      i++;
      continue;
    }
    
    // Détecter "Résultats du tournoi" suivi de lignes "N- pseudo" et "Votre résultat"
    if (msg.isSystem && /^R[ée]sultats du tournoi/i.test(msg.text.trim())) {
      const blockMessages: TableChatMessage[] = [msg];
      let j = i + 1;
      while (j < messages.length) {
        const nextMsg = messages[j];
        if (!nextMsg.isSystem) break;
        const txt = nextMsg.text.trim();
        // Lignes de classement "N- pseudo" ou "Votre résultat"
        if (/^\d+[-–]\s*\S/.test(txt) || /^Votre r[ée]sultat/i.test(txt)) {
          blockMessages.push(nextMsg);
          j++;
        } else {
          break;
        }
      }
      if (blockMessages.length > 1) {
        result.push({
          type: 'tournament-results',
          messages: blockMessages,
          key: `tournament-${blockMessages[0].id}`,
        });
        i = j;
        continue;
      }
    }
    
    // Message normal
    result.push({
      type: 'message',
      messages: [msg],
      key: msg.id,
    });
    i++;
  }
  
  return result;
}

// ============================================
// Page principale du jeu
// ============================================

export default function Game({ session, gameInfo }: GameProps) {
  const { endGame, setGameNotification, showToast, activeTab, preferences, updateGameInfo } = useApp();
  const resolvedTheme = useResolvedTheme(preferences.theme);
  
  // Extraire les infos de connexion depuis gameInfo (remplace useParams/useLocation)
  const ip = gameInfo.ip?.toString();
  const port = gameInfo.port?.toString();
  const routeServerConnId = gameInfo.serverConnectionId || null;
  
  // Mode différé rétro classique
  const isRetroDiffereMode = !!gameInfo.retroDiffere;
  const retroInfo = gameInfo.retroDiffere;
  const retroSessionId = retroInfo?.sessionId ?? 0;
  const retroDealIndex = retroInfo?.dealIndex ?? 0;
  const retroRejoin = retroInfo?.rejoin ?? false;
  const retroTotalDeals = retroInfo?.totalDeals;
  const gameSidebarTitle = isRetroDiffereMode
    ? `Différé Rétro — Donne ${retroDealIndex + 1}${retroTotalDeals ? `/${retroTotalDeals}` : ''}`
    : 'Partie en cours';
  
  // Mode Neo : table gérée entièrement par le backend Node.js
  const isNeoMode = !!gameInfo.neo && !isRetroDiffereMode;
  const neoTableId = gameInfo.neoTableId || '';
  const isSpectatorMode = !!gameInfo.spectatorMode;
  const spectatorSourceId = gameInfo.spectatorSourceId ?? (neoTableId ? `neo:${neoTableId}` : null);
  const [spectatorLiveWarning, setSpectatorLiveWarning] = useState<string | null>(null);
  
  // Mode "table créée" : on a un serverConnectionId au lieu de ip/port
  const isCreatedMode = !isNeoMode && !isRetroDiffereMode && !!routeServerConnId;
  
  // Calculer le tableId
  const tableId = isRetroDiffereMode
    ? `retro-${retroSessionId}-${retroDealIndex}`
    : isNeoMode
      ? `neo-${neoTableId}`
      : isCreatedMode 
        ? `created-${routeServerConnId}` 
        : (ip && port ? `${ip}:${port}` : 'unknown');
  
  // Neo config state (stored from neo:state)
  const neoConfigRef = useRef<{ petitesAutorisees: boolean }>({ petitesAutorisees: true });
  const [neoMatchFinished, setNeoMatchFinished] = useState(false);
  const retroContinueSentRef = useRef(false);
  const retroAwaitingManualContinueRef = useRef(false);
  const [showRetroContinueHint, setShowRetroContinueHint] = useState(false);
  
  // ============================================
  // NOUVEAU SYSTÈME: Queue d'actions
  // ============================================
  const {
    gameState: queueGameState,
    queueLength,
    isProcessing,
    enqueueServerMessage,
    enqueueAction,
    skipAnimations,
    skipPendingChienAck,
    clearQueue: _clearQueue,
    resetForFullSync,
    setPosition,
    getGameHistory,
  } = useGameQueue({ tableId, myPseudo: session.pseudo });
  void _clearQueue;
  const resetForFullSyncRef = useRef(resetForFullSync);
  resetForFullSyncRef.current = resetForFullSync;

  useEffect(() => {
    updateGameInfo({
      hasOtherHumans: computeHasOtherHumansForDnd(gameInfo, queueGameState.hasOtherHumans),
    });
  }, [gameInfo, queueGameState.hasOtherHumans, updateGameInfo]);
  
  // Convertir QueueGameState en GameState local pour compatibilité
  const convertToLocalGameState = useCallback((qs: QueueGameState): GameState => {
    const liveTable = gameInfo.idserv === 'live';
    return {
      phase: qs.phase,
      tableId: qs.tableId,
      idPartie: qs.idPartie,
      players: qs.players.map(p => ({
        position: p.position,
        pseudo: formatTablePlayerName(
          p.name,
          p.position,
          qs.myPosition,
          qs.playerCount,
          liveTable,
        ),
        isConnected: p.isConnected,
        isReady: true,
        tricksWon: p.tricksWon || 0,
        bid: p.lastBid,
        isPartner: p.isPartner,
      })),
      myPosition: qs.myPosition,
      myHand: qs.myHand,
      currentTrick: qs.currentTrick,
      trickPlayerPositions: qs.trickPlayerPositions,
      playerCount: qs.playerCount,
      currentPlayer: qs.currentPlayer,
      dealer: qs.dealer,
      entameur: qs.entameur,
      taker: qs.taker,
      currentContract: qs.currentContract,
      calledKing: qs.calledKing,
      partner: qs.partner,
      partnerRevealed: qs.partnerRevealed,
      lastTrick: qs.lastTrick,
      chien: qs.chien,
      numPli: qs.numPli,
      scores: qs.scores,  // Données du score en fin de donne
    };
  }, [gameInfo.idserv]);
  
  // État de jeu converti pour les composants UI
  const gameState: GameState | null = useMemo(() => {
    if (queueGameState.phase === 0) return null;
    return convertToLocalGameState(queueGameState);
  }, [queueGameState, convertToLocalGameState]);

  useEffect(() => {
    if (!gameState) {
      setBetaPageDetail(null);
      return;
    }
    setBetaPageDetail({
      page: 'game',
      phase: gameState.phase,
      tableId: gameState.tableId ?? null,
      idPartie: gameState.idPartie ?? null,
      playerCount: gameState.playerCount,
      numPli: gameState.numPli ?? null,
      currentContract: gameState.currentContract ?? null,
      currentPlayer: gameState.currentPlayer ?? null,
      myPosition: gameState.myPosition ?? null,
      taker: gameState.taker ?? null,
      dealer: gameState.dealer ?? null,
      spectatorMode: gameInfo.spectatorMode ?? false,
      idserv: gameInfo.idserv ?? null,
      neo: gameInfo.neo ?? false,
    });
    return () => setBetaPageDetail(null);
  }, [gameInfo.idserv, gameInfo.neo, gameInfo.spectatorMode, gameState]);
  
  // ============================================
  // Tapis de jeu dynamique (cards-plis)
  // ============================================

  const toScreenPosition = useCallback((playerPos: number, myPos: number, count: number): ScreenPosition => {
    const rel = (playerPos - myPos + count) % count;
    const map: Record<number, Record<number, ScreenPosition>> = {
      3: { 0: 'south', 1: 'east', 2: 'west' },
      4: { 0: 'south', 1: 'east', 2: 'north', 3: 'west' },
      5: { 0: 'south', 1: 'east', 2: 'north-east', 3: 'north-west', 4: 'west' },
    };
    return map[count]?.[rel] || 'south';
  }, []);

  const [bgTrickEvents, setBgTrickEvents] = useState<TrickEndEvent[]>([]);
  const [bgChienEvent, setBgChienEvent] = useState<ChienEvent | undefined>();
  const bgLastNumPli = useRef(-1);
  const bgLastPhase = useRef(0);

  // Reset on new deal (phase goes back to bidding)
  useEffect(() => {
    if (!gameState) return;
    if (gameState.phase === 2 || gameState.phase === 0) {
      setBgTrickEvents([]);
      setBgChienEvent(undefined);
      bgLastNumPli.current = -1;
      bgLastPhase.current = 0;
    }
  }, [gameState?.phase]);

  // Detect trick completions
  useEffect(() => {
    if (!gameState || preferences.tableOverlay !== 'cards-plis') return;
    const pli = gameState.numPli ?? 0;
    if (pli > 0 && pli !== bgLastNumPli.current) {
      bgLastNumPli.current = pli;
      const winnerPos = gameState.entameur ?? 0;
      let pileOwnerPos = winnerPos;
      // 5p : plis du partenaire révélé → tas du preneur
      if (
        gameState.playerCount === 5 &&
        gameState.partnerRevealed &&
        gameState.partner !== undefined &&
        gameState.taker !== undefined &&
        winnerPos === gameState.partner
      ) {
        pileOwnerPos = gameState.taker;
      }
      const screenPos = toScreenPosition(
        pileOwnerPos,
        gameState.myPosition,
        gameState.playerCount,
      );
      setBgTrickEvents(prev => [...prev, {
        winnerScreenPos: screenPos,
        cardCount: gameState.playerCount,
      }]);
    }
  }, [
    gameState?.numPli,
    gameState?.entameur,
    gameState?.myPosition,
    gameState?.playerCount,
    gameState?.partnerRevealed,
    gameState?.partner,
    gameState?.taker,
    preferences.tableOverlay,
    toScreenPosition,
  ]);

  // Detect écart / chien placement
  useEffect(() => {
    if (!gameState || preferences.tableOverlay !== 'cards-plis') return;
    const phase = gameState.phase;
    const prevPhase = bgLastPhase.current;
    bgLastPhase.current = phase;

    // Phase 5→6 : écart done → place cards in taker's zone
    // Phase 4→6 : garde sans/contre → chien placed
    if (phase === 6 && prevPhase > 0 && prevPhase < 6 && gameState.taker !== undefined) {
      const takerScreen = toScreenPosition(gameState.taker, gameState.myPosition, gameState.playerCount);
      const contract = gameState.currentContract ?? 0;
      const chienSize = gameState.playerCount === 5 ? 3 : 6;

      if (contract >= Contract.GardeContre) {
        setBgChienEvent({
          takerScreenPos: takerScreen,
          cardCount: chienSize,
          excludeTaker: true,
        });
      } else if (contract >= Contract.GardeSans) {
        setBgChienEvent({
          takerScreenPos: takerScreen,
          cardCount: chienSize,
        });
      } else {
        setBgChienEvent({
          takerScreenPos: takerScreen,
          cardCount: chienSize,
        });
      }
    }
  }, [gameState?.phase, gameState?.taker, gameState?.myPosition, gameState?.playerCount, gameState?.currentContract, preferences.tableOverlay, toScreenPosition]);

  // ============================================
  // États de connexion et UI
  // ============================================
  const [socket, setSocket] = useState<Socket | null>(null);
  const spectatorPlayback = useSpectatorPlayback(
    isSpectatorMode ? socket : null,
    isSpectatorMode ? spectatorSourceId : null,
  );
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [serverConnectionId, _setServerConnectionId] = useState<string | null>(routeServerConnId || null);
  void _setServerConnectionId; // Prévu pour usage futur
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [isReady, setIsReady] = useState(false);
  
  // Replay mode
  const [showReplay, setShowReplay] = useState(false);
  
  // Library modal
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [replayHistory, setReplayHistory] = useState<GameHistory | null>(null);
  
  // Cartes sélectionnées pour l'écart
  const [selectedCards, setSelectedCards] = useState<CardNumber[]>([]);
  const ecartFlyRef = useRef<{
    card: CardNumber;
    rect: DOMRect;
    rotation: number;
    direction: 'to-table' | 'to-hand';
  } | null>(null);

  const applyEcartSelection = useCallback((cards: CardNumber[]) => {
    const added = cards.find(c => !selectedCards.includes(c));
    const removed = selectedCards.find(c => !cards.includes(c));
    if (added) {
      const cap = captureCardRect(added, '.player-hand-area');
      if (cap) ecartFlyRef.current = { card: added, ...cap, direction: 'to-table' };
    } else if (removed) {
      const cap = captureCardRect(removed, '.ecart-cards');
      if (cap) ecartFlyRef.current = { card: removed, ...cap, direction: 'to-hand' };
    }
    setSelectedCards(cards);
  }, [selectedCards]);

  useLayoutEffect(() => {
    const fly = ecartFlyRef.current;
    if (!fly) return;
    ecartFlyRef.current = null;
    const sel = fly.direction === 'to-table'
      ? `.ecart-card[data-ecart-card="${fly.card}"]`
      : `.player-hand-area .hand-card-wrapper[data-card-number="${fly.card}"]`;
    const el = document.querySelector(sel) as HTMLElement | null;
    if (!el) return;
    const handArea = fly.direction === 'to-hand'
      ? document.querySelector('.player-hand-area')
      : null;
    handArea?.classList.add('player-hand-area-flying');
    // Sur l'arc, le wrapper englobe le pivot : on mesure la carte visible.
    const measureEl = fly.direction === 'to-hand'
      ? (el.querySelector('.card') as HTMLElement | null)
      : null;
    playEcartFlip(el, fly, fly.direction === 'to-table', measureEl);
    window.setTimeout(() => {
      handArea?.classList.remove('player-hand-area-flying');
    }, ECART_FLY_MS + 40);
  }, [selectedCards]);
  
  // Poignée
  const [showPoigneeProposal, setShowPoigneeProposal] = useState(false);  // Afficher la proposition de poignée
  const [poigneeSelectedCards, setPoigneeSelectedCards] = useState<CardNumber[]>([]);  // Cartes sélectionnées pour la poignée
  const [pendingPoignee, setPendingPoignee] = useState<{ type: PoigneeType; cards: CardNumber[] } | null>(null);  // Poignée validée, en attente d'envoi avec la carte
  const poigneeCheckedRef = useRef(false);  // true si on a déjà vérifié la poignée pour ce pli 1
  
  // Capture de la position DOM de la carte jouée (pour l'animation FLIP)
  const playedCardOriginRef = useRef<{ rect: DOMRect; rotation: number } | null>(null);
  
  const getLocalCardOrigin = useCallback(() => {
    const origin = playedCardOriginRef.current;
    playedCardOriginRef.current = null;
    return origin;
  }, []);
  
  // Chat de table
  const [tableChatMessages, setTableChatMessages] = useState<TableChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [availableEmoticons, setAvailableEmoticons] = useState<Array<{ text: string; emoji: string; description: string }>>([]);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  
  // ============================================
  // Mode Live - État du tournoi
  // ============================================
  // idserv provient de gameInfo (remplace location.state)
  const gameIdserv = gameInfo.idserv || null;
  const isDuplicate = session.compet && gameIdserv !== 'libre';
  const useDupTable = isDuplicate && preferences.duplicateTableEnabled;
  const DUP_TO_BASE: Record<DuplicateTableStyle, TableBase> = {
    'dup-felt': 'felt', 'dup-neo': 'neo', 'dup-legacy': 'legacy-v2',
  };
  const effectiveTableBase = useDupTable ? DUP_TO_BASE[preferences.duplicateTableStyle] : preferences.tableBase;
  const effectiveTableEmblem = useDupTable ? 'none' as const : preferences.tableEmblem;
  const [isLiveMode, setIsLiveMode] = useState(gameIdserv === 'live');
  const [liveDonnes, setLiveDonnes] = useState<{ current: number; max: number } | null>(null);
  const [nextTournamentAt, setNextTournamentAt] = useState<number | null>(null);
  const [nextDealAt, setNextDealAt] = useState<number | null>(null);
  const [showCardCountdown, setShowCardCountdown] = useState(false);
  const handleCardCountdownComplete = useCallback(() => setShowCardCountdown(false), []);
  const [liveClassement, setLiveClassement] = useState<LivePlayerInfo[]>([]);
  const [sidebarView, setSidebarView] = useState<'chat' | 'classement'>(
    gameIdserv === 'live' ? 'classement' : 'chat'
  );
  const [classementSortBy, setClassementSortBy] = useState<ClassementSortBy>('clTournoi');
  const [systemToasts, setSystemToasts] = useState<Toast[]>([]);
  const sidebarViewRef = useRef(sidebarView);
  sidebarViewRef.current = sidebarView;
  const isLiveModeRef = useRef(isLiveMode);
  isLiveModeRef.current = isLiveMode;
  const queueGameStateRef = useRef(queueGameState);
  queueGameStateRef.current = queueGameState;
  
  // Ref pour activeTab (évite les closures stales dans les callbacks)
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  
  // ============================================
  // Notifications cross-onglets
  // ============================================
  
  // Notifier quand c'est au tour du joueur et qu'il n'est pas sur l'onglet Game
  useEffect(() => {
    if (!gameState) return;
    const isMyTurn = gameState.currentPlayer === gameState.myPosition && gameState.phase === 6;
    if (isMyTurn) {
      setGameNotification({ type: 'your_turn', message: 'À toi de jouer !' });
      if (activeTabRef.current !== 'game') {
        showToast({ message: 'À toi de jouer !', type: 'game' });
      }
    }
  }, [gameState?.currentPlayer, gameState?.myPosition, gameState?.phase, setGameNotification, showToast]);
  
  // Notifier quand une nouvelle donne commence
  useEffect(() => {
    if (!gameState || gameState.phase !== 2) return; // Phase 2 = enchères = début de donne
    if (activeTabRef.current !== 'game') {
      setGameNotification({ type: 'new_game', message: 'Nouvelle donne !' });
      showToast({ message: 'Une nouvelle donne débute !', type: 'game' });
    }
  }, [gameState?.phase, setGameNotification, showToast]);
  
  // Effacer la notification quand l'utilisateur revient sur l'onglet Game
  useEffect(() => {
    if (activeTab === 'game') {
      setGameNotification(null);
    }
  }, [activeTab, setGameNotification]);
  
  const { isCompact, isPortrait, isLandscape } = useResponsive();
  const isSidebarVisible = !isCompact || isPortrait;
  // Attente live : une donne tourne déjà, on n'a pas encore de cartes.
  const isLiveDealWaiting = Boolean(
    isLiveMode && !gameState && liveDonnes && !showCardCountdown && !nextTournamentAt
  );
  const livePlayersFinished = useMemo(() => {
    if (liveClassement.length === 0) return null;
    const finished = liveClassement.filter(isLivePlayerFinished).length;
    return { finished, total: liveClassement.length };
  }, [liveClassement]);
  const gameLayoutClass = [
    'chat-container game-layout',
    isPortrait ? 'game-layout--portrait-dock' : '',
    // Sur le live, le décompte 5–1 ne doit pas masquer le dock.
    isPortrait && showCardCountdown && !isLiveMode ? 'game-layout--portrait-expand' : '',
    isLiveDealWaiting && isSidebarVisible ? 'game-layout--live-waiting' : '',
  ].filter(Boolean).join(' ');
  const showGameDrawer = isCompact && isLandscape;
  const hoistWaitingToasts = isLiveDealWaiting && isSidebarVisible;

  // Ref pour savoir si l'utilisateur veut explicitement quitter
  const wantsToLeaveRef = useRef(false);
  
  // Réinitialiser isReady quand une nouvelle donne commence
  const prevPhaseRef = useRef<number | null>(null);
  const prevHandRef = useRef<string>('');
  useEffect(() => {
    if (gameState) {
      const currentHandKey = gameState.myHand.join(',');
      
      // Ne pas considérer un tableau vide comme une main valide pour la comparaison
      // Car resetStateForNewDeal passe par un état intermédiaire avec myHand = []
      // avant que l'action distribue n'ajoute les vraies cartes
      if (currentHandKey === '') {
        // État intermédiaire avec main vide, on ignore
        return;
      }
      
      const handChanged = prevHandRef.current !== '' && prevHandRef.current !== currentHandKey;
      
      // Reset si les cartes en main ont changé -> c'est une nouvelle distribution
      // Peu importe la phase (même si la nouvelle partie finit aussi en 99999 car tout le monde passe)
      if (handChanged) {
        log.debug('[Game] Hand changed - new deal, resetting isReady', { 
          prevPhase: prevPhaseRef.current, 
          newPhase: gameState.phase,
        });
        setIsReady(false);
        setSelectedCards([]);
        // Reset poignée state
        setShowPoigneeProposal(false);
        setPoigneeSelectedCards([]);
        setPendingPoignee(null);
        poigneeCheckedRef.current = false;
        // Close replay if open when a new deal starts
        setShowReplay(false);
        setReplayHistory(null);
      }
      
      prevPhaseRef.current = gameState.phase;
      prevHandRef.current = currentHandKey;
    }
  }, [gameState?.phase, gameState?.myHand]);

  // Snapshot d'état pour debug (activable via localStorage wt_debug=1)
  useEffect(() => {
    if (!gameState) return;
    log.debug('[Game] State snapshot', {
      idPartie: gameState.idPartie,
      phase: gameState.phase,
      myPosition: gameState.myPosition,
      currentPlayer: gameState.currentPlayer,
      handLen: gameState.myHand.length,
      queueLength,
      isProcessing,
      hasScores: !!gameState.scores,
      hasChien: !!gameState.chien,
    });
  }, [
    gameState?.idPartie,
    gameState?.phase,
    gameState?.myPosition,
    gameState?.currentPlayer,
    gameState?.myHand?.length,
    gameState?.scores,
    gameState?.chien,
    queueLength,
    isProcessing,
  ]);
  
  // ============================================
  // Handlers pour les messages Live
  // ============================================
  
  const handleLiveMessage = useCallback((msgType: string, vals: any) => {
    log.debug(`[Game] Live message: ${msgType}`, vals);
    
    switch (msgType) {
      case 'liveDonnes': {
        const current = parseInt(vals.nb, 10) || 0;
        const max = parseInt(vals.max, 10) || 0;
        setIsLiveMode(true);
        setLiveDonnes({ current, max });
        // Une nouvelle donne commence : masquer les timers et le décompte
        setNextDealAt(null);
        setShowCardCountdown(false);
        setNextTournamentAt(null);
        // Basculer automatiquement sur le classement en mode live
        if (sidebarViewRef.current === 'chat') {
          setSidebarView('classement');
        }
        break;
      }
      
      case 'liveClear': {
        setLiveClassement([]);
        break;
      }
      
      case 'liveJoueurInfos': {
        const info: LivePlayerInfo = {
          pseudo: vals.pseudo || '',
          etat: vals.etat || '-',
          clDonne: vals.clDonne || '-',
          pointsTournoi: vals.pointsTournoi || '0',
          clTournoi: vals.clTournoi || '-',
          clGeneral: vals.clGeneral || '-',
        };
        setLiveClassement(prev => {
          // Upsert : remplacer si pseudo existant, sinon ajouter
          const existing = prev.findIndex(p => p.pseudo === info.pseudo);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = info;
            return updated;
          }
          return [...prev, info];
        });
        break;
      }
      
      case 'liveJoueurEtat': {
        const pseudo = vals.pseudo || '';
        const newEtat = vals.etat || '-';
        setLiveClassement(prev => {
          const idx = prev.findIndex(p => p.pseudo === pseudo);
          if (idx < 0) return prev;
          const updated = [...prev];
          const player = { ...updated[idx] };
          
          // Mettre à jour l'état
          player.etat = newEtat;
          
          // Si le joueur a fini (etat est numérique et clDonne est "-"),
          // le tri sera automatiquement recalculé dans LiveClassement
          updated[idx] = player;
          return updated;
        });
        break;
      }
      
      case 'liveJoueurPart': {
        const pseudo = vals.pseudo || '';
        setLiveClassement(prev => prev.filter(p => p.pseudo !== pseudo));
        // Toast notification de départ
        if (pseudo) {
          setSystemToasts(prev => [...prev, {
            id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            text: `__LEAVE__${pseudo}`,
            type: 'info' as const,
            createdAt: Date.now(),
          }]);
        }
        break;
      }
      
      case 'liveJoueurArrive': {
        const pseudo = vals.pseudo || '';
        const info: LivePlayerInfo = {
          pseudo,
          etat: '-',
          clDonne: '-',
          pointsTournoi: '0',
          clTournoi: '-',
          clGeneral: vals.clGeneral || '-',
        };
        setLiveClassement(prev => {
          // Ne pas ajouter si déjà présent
          if (prev.some(p => p.pseudo === info.pseudo)) return prev;
          return [...prev, info];
        });
        // Toast notification d'arrivée
        if (pseudo) {
          setSystemToasts(prev => [...prev, {
            id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            text: `__JOIN__${pseudo}`,
            type: 'info' as const,
            createdAt: Date.now(),
          }]);
        }
        break;
      }
      
      case 'liveJoueurMajGeneral': {
        const pseudo = vals.pseudo || '';
        const clGeneral = vals.clGeneral || '-';
        setLiveClassement(prev => {
          const idx = prev.findIndex(p => p.pseudo === pseudo);
          if (idx < 0) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], clGeneral };
          return updated;
        });
        break;
      }
      
      case 'liveProchaiTournoi': {
        const delaiMinute = parseInt(vals.delaiMinute || '0', 10);
        if (delaiMinute > 0) {
          setNextTournamentAt(Date.now() + delaiMinute * 60 * 1000);
        }
        break;
      }
    }
  }, []);
  
  // ============================================
  // Helpers pour les toasts système
  // ============================================
  
  const addSystemToast = useCallback((text: string) => {
    // Détecter si c'est un message de résultat (Top, Bulle)
    const isResult = /^(Top|Bulle)\s*:/i.test(text);
    const newToast: Toast = {
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      text,
      type: isResult ? 'result' : 'info',
      createdAt: Date.now(),
    };
    setSystemToasts(prev => [...prev, newToast]);
  }, []);
  
  const dismissToast = useCallback((id: string) => {
    setSystemToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  
  // Connexion Socket.IO
  useEffect(() => {
    log.debug('[Game] Initializing socket connection to', BACKEND_URL);
    
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
    });
    
    newSocket.on('connect', () => {
      log.debug('[Game] Socket connected, id:', newSocket.id);
    });
    
    newSocket.on('connect_error', (err) => {
      log.error('[Game] Socket connection error:', err);
      setError('Erreur de connexion au serveur');
      setIsConnecting(false);
    });
    
    newSocket.on('disconnect', (reason) => {
      log.debug('[Game] Socket disconnected:', reason);
    });

    if (isRetroDiffereMode) {
      // ============================
      // RETRO DIFFÉRÉ: Socket listeners (via neoAdapter)
      // ============================

      const applyRetroFullState = (data: { state?: NeoStatePayload }) => {
        if (!data.state) return;
        resetForFullSyncRef.current();
        neoConfigRef.current = { petitesAutorisees: data.state.config.petitesAutorisees };
        retroContinueSentRef.current = false;
        retroAwaitingManualContinueRef.current = false;
        setShowRetroContinueHint(false);
        const actions = neoStateToActions(data.state, session.pseudo);
        for (const action of actions) {
          enqueueAction(action);
        }
        setConnectionId(`retro-${retroSessionId}`);
        setIsConnecting(false);
      };

      newSocket.on('retro-differe:deal_started', (data: { state?: NeoStatePayload }) => {
        log.debug('[Game/Retro] Deal started');
        applyRetroFullState(data);
      });

      newSocket.on('retro-differe:state_update', (data: { state?: NeoStatePayload }) => {
        log.debug('[Game/Retro] State update, phase:', data.state?.phase);
        applyRetroFullState(data);
      });

      newSocket.on('retro-differe:deal', (data: any) => {
        log.debug('[Game/Retro] Deal received, hand:', data.hand?.length);
        const actions = neoDealToActions(data, neoConfigRef.current.petitesAutorisees);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('retro-differe:bid_made', (data: any) => {
        const actions = neoBidToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:all_passed', () => {
        const actions = neoAllPassedToActions();
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:king_called', (data: any) => {
        const actions = neoKingCalledToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:chien', (data: any) => {
        const actions = neoChienToActions(data);
        for (const action of actions) enqueueAction(action);
        if (data.cards?.length > 0) {
          window.setTimeout(() => {
            newSocket.emit('retro-differe:chien_acknowledged');
          }, DELAYS.CHIEN_SHOWN);
        }
      });

      newSocket.on('retro-differe:ecart_done', (data: any) => {
        const actions = neoEcartDoneToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:hand_updated', (data: { hand: number[] }) => {
        enqueueAction({ type: 'hand_update', hand: data.hand });
      });

      newSocket.on('retro-differe:card_played', (data: any) => {
        const actions = neoCardPlayedToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:poignee_declared', (data: any) => {
        const actions = neoPoigneeDeclaredToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:trick_end', (data: any) => {
        const actions = neoTrickEndToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:scores', (data: any) => {
        const actions = neoScoresToActions(data);
        for (const action of actions) enqueueAction(action);
      });

      newSocket.on('retro-differe:deal_finished', () => {
        // Classement détaillé via retro-differe:standing
      });

      newSocket.on('retro-differe:info', (data: { message?: string }) => {
        if (data.message) {
          showToast({ type: 'game', message: data.message, duration: 5000 });
        }
      });

      newSocket.on('retro-differe:standing', (data: {
        messages?: { standing?: string; perDeal?: string | null };
      }) => {
        if (data.messages?.perDeal) {
          showToast({ type: 'game', message: data.messages.perDeal, duration: 8000 });
        }
        if (data.messages?.standing) {
          showToast({ type: 'game', message: data.messages.standing, duration: 10000 });
        }
      });

      newSocket.on('retro-differe:error', (data: { message?: string }) => {
        log.error('[Game/Retro] Error:', data);
        setError(data.message ?? 'Erreur différé rétro');
        setIsConnecting(false);
      });

    } else if (isNeoMode) {
      // ============================
      // NEO MODE: Socket listeners
      // ============================

      if (isSpectatorMode) {
        newSocket.on('neo:spectator_state', (data: { players: Array<{ pseudo: string; position: number }>; config: { playerCount?: number } }) => {
          setConnectionId(neoTableId);
          setIsConnecting(false);
          void data;
        });
        newSocket.on('neo:error', (data: { message?: string }) => {
          setError(data.message ?? 'Erreur spectateur');
          setIsConnecting(false);
        });
      } else {
      newSocket.on('neo:spectator_live_warning', (data: { message?: string }) => {
        setSpectatorLiveWarning(data.message ?? 'Des spectateurs regardent la partie en direct.');
      });
      newSocket.on('neo:spectator_count', (data: { count: number }) => {
        if (data.count <= 0) setSpectatorLiveWarning(null);
      });

      newSocket.on('neo:state', (data: NeoStatePayload) => {
        log.debug('[Game/Neo] State received, phase:', data.phase);
        neoConfigRef.current = { petitesAutorisees: data.config.petitesAutorisees };
        setNeoMatchFinished(data.phase === 'finished');
        const actions = neoStateToActions(data, session.pseudo);
        for (const action of actions) {
          enqueueAction(action);
        }
        setConnectionId(neoTableId);
        setIsConnecting(false);
      });

      newSocket.on('neo:deal', (data: any) => {
        log.debug('[Game/Neo] Deal received, hand:', data.hand?.length);
        setNeoMatchFinished(false);
        const actions = neoDealToActions(data, neoConfigRef.current.petitesAutorisees);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:bid_made', (data: any) => {
        log.debug('[Game/Neo] Bid made:', data);
        const actions = neoBidToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:all_passed', () => {
        log.debug('[Game/Neo] All passed');
        const actions = neoAllPassedToActions();
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:king_called', (data: any) => {
        log.debug('[Game/Neo] King called:', data);
        const actions = neoKingCalledToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:chien', (data: any) => {
        log.debug('[Game/Neo] Chien:', data);
        const actions = neoChienToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:ecart_done', (data: any) => {
        log.debug('[Game/Neo] Ecart done:', data);
        const actions = neoEcartDoneToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:hand_updated', (data: { hand: number[] }) => {
        log.debug('[Game/Neo] Hand updated:', data.hand?.length);
        enqueueAction({ type: 'hand_update', hand: data.hand });
      });

      newSocket.on('neo:card_played', (data: any) => {
        log.debug('[Game/Neo] Card played:', data);
        const actions = neoCardPlayedToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:poignee_declared', (data: any) => {
        log.debug('[Game/Neo] Poignee declared:', data);
        const actions = neoPoigneeDeclaredToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:trick_end', (data: any) => {
        log.debug('[Game/Neo] Trick end:', data);
        const actions = neoTrickEndToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:scores', (data: any) => {
        log.debug('[Game/Neo] Scores:', data);
        const actions = neoScoresToActions(data);
        for (const action of actions) {
          enqueueAction(action);
        }
      });

      newSocket.on('neo:match_finished', () => {
        log.debug('[Game/Neo] Match finished');
        setNeoMatchFinished(true);
      });

      newSocket.on('neo:player_joined', (data: { pseudo: string; position: number }) => {
        log.debug('[Game/Neo] Player joined:', data);
      });

      newSocket.on('neo:player_left', (data: { pseudo: string; position: number }) => {
        log.debug('[Game/Neo] Player left:', data);
      });

      newSocket.on('neo:chat_message', (data: { from: string; message: string; emoticon?: string; timestamp: number }) => {
        const newMessage: TableChatMessage = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          pseudo: data.from,
          text: data.message,
          timestamp: data.timestamp,
          isSystem: false,
          isOwn: data.from === session.pseudo,
        };
        setTableChatMessages(prev => [...prev, newMessage]);
      });

      newSocket.on('neo:error', (data: { code: string; message: string }) => {
        log.error('[Game/Neo] Error:', data);
        if (data.code === 'TABLE_NOT_FOUND') {
          setError('Table introuvable');
          setIsConnecting(false);
        } else if (data.code === 'TABLE_FULL' || data.code === 'CANNOT_START') {
          setError(data.message ?? 'Impossible de rejoindre la table');
          setIsConnecting(false);
        }
      });
      } // end non-spectator neo listeners

    } else {
      // ============================
      // LEGACY MODE: Socket listeners
      // ============================

    // Messages du serveur de jeu - TOUS passent par la queue d'actions
    // Note: On filtre les messages 'pli' et 'fin_pli' car ils arrivent aussi via game:trick_end
    newSocket.on('game:message', (data: { connectionId: string; message: any }) => {
      const msgType = data.message?.type;
      log.debug(`[Game] Message received: type=${msgType}`);
      
      // Live messages - traités directement, PAS via la queue d'actions
      const liveTypes = ['liveDonnes', 'liveClear', 'liveJoueurInfos', 
                         'liveJoueurEtat', 'liveJoueurPart', 'liveJoueurArrive', 'liveJoueurMajGeneral',
                         'liveProchaiTournoi'];
      if (liveTypes.includes(msgType)) {
        handleLiveMessage(msgType, data.message.vals);
        return;
      }
      
      // Distribue : une nouvelle donne démarre → nettoyer les timers
      if (msgType === 'distribue' || msgType === 'distribue_cartes') {
        log.debug('📢 [Game] *** DISTRIBUE RECEIVED ***', {
          cartes: data.message?.vals?.cartes?.length,
          etat: data.message?.vals?.etat,
          contrats: data.message?.vals?.contrats,
        });
        setNextDealAt(null);
        setShowCardCountdown(false);
        setNextTournamentAt(null);
      }
      
      // Score/fin de donne : en mode live, envoyer automatiquement "prêt"
      if (msgType === 'score' && isLiveModeRef.current) {
        log.debug('[Game] Live mode: auto-sending ready after score');
        // Petit délai pour laisser le score s'afficher brièvement
        setTimeout(() => {
          if (newSocket.connected) {
            newSocket.emit('game:ready', {
              connectionId: data.connectionId,
            });
          }
        }, 500);
      }
      
      // Ignorer pli/fin_pli ici car ils sont traités via game:trick_end
      if (msgType === 'pli' || msgType === 'fin_pli') {
        log.debug('[Game] Ignoring pli/fin_pli from game:message (handled by game:trick_end)');
        return;
      }
      
      if (msgType && data.message?.vals !== undefined) {
        enqueueServerMessage(data.message);
      }
    });
    
    // Fin de pli (émis séparément par le backend)
    newSocket.on('game:trick_end', (data: { connectionId: string; trick: any }) => {
      log.debug('[Game] Trick end received:', JSON.stringify(data.trick));
      enqueueServerMessage({ type: 'fin_pli', vals: data.trick });
    });
    
    // Erreurs
    newSocket.on('game:error', (data: { error: string }) => {
      const err = data.error || '';
      const phase = queueGameStateRef.current.phase;
      // Fin de donne / live : une action peut arriver après déconnexion serveur
      if (err.includes('Non connecté') && (phase === 6 || phase === 99999)) {
        log.warn('[Game] Ignoring disconnect error at end of deal:', err);
        return;
      }
      log.error('[Game] Game error:', err);
      setError(err);
      setIsConnecting(false);
    });
    
    // Connexion réussie
    newSocket.on('game:joined', (data: { connectionId: string }) => {
      log.debug('[Game] Successfully joined, connectionId:', data.connectionId);
      setConnectionId(data.connectionId);
      setIsConnecting(false);
    });
    
    // Chat de table - messages des autres joueurs et système
    newSocket.on('game:table_chat', (data: { 
      connectionId: string; 
      chat: { auteur?: string; texte?: string; isSystem?: boolean } 
    }) => {
      log.debug('[Game] Table chat received:', data.chat);
      const { auteur, texte, isSystem } = data.chat;
      if (!texte) return;
      
      // isSystem peut être fourni par le backend, sinon on le déduit de l'auteur
      const isSystemMessage = isSystem === true || !auteur || auteur === '';
      
      const newMessage: TableChatMessage = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        pseudo: isSystemMessage ? '' : (auteur || ''),
        text: texte,
        timestamp: Date.now(),
        isSystem: isSystemMessage,
        isOwn: false,  // Les messages de l'utilisateur sont ajoutés localement, pas via ce handler
      };
      
      setTableChatMessages(prev => [...prev, newMessage]);
      
      // Intercepter les messages système avec timer (indépendamment de la sidebar)
      if (isSystemMessage) {
        // "Démarrage de la donne xx/yy dans zz secondes", "nouvelle donne commence dans N secondes",
        // "La donne n°X/Y commence dans N secondes"
        const dealTimerMatch = texte.match(/(?:D[ée]marrage de la donne|donne.*commence|nouvelle donne commence).*dans\s+(\d+)\s+seconde/i);
        if (dealTimerMatch) {
          const sec = parseInt(dealTimerMatch[1], 10);
          if (sec > 0) {
            setNextDealAt(Date.now() + sec * 1000);
            setShowCardCountdown(true);
          }
        }
        // "Le prochain tournoi ... dans N minutes" → timer (fallback si liveProchaiTournoi manqué)
        const tournoiChatMatch = texte.match(/prochain tournoi.*dans\s+(\d+)\s+minute/i);
        if (tournoiChatMatch) {
          const min = parseInt(tournoiChatMatch[1], 10);
          if (min > 0) setNextTournamentAt(Date.now() + min * 60 * 1000);
        }
      }
      
      // Si c'est un message système et que le chat n'est pas visible, afficher en toast
      if (isSystemMessage && sidebarViewRef.current !== 'chat') {
        // Ignorer les lignes de tirets
        if (/^-{3,}$/.test(texte.trim())) {
          // skip
        }
        // Messages "- xxx" : regrouper avec le toast précédent
        else if (/^-\s/.test(texte)) {
          const cleaned = texte.replace(/^-\s/, '');
          setSystemToasts(prev => {
            const last = prev[prev.length - 1];
            if (last && (Date.now() - last.createdAt) < 2000) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, text: last.text + '\n' + cleaned };
              return updated;
            }
            return [...prev, { id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`, text: cleaned, type: 'info' as const, createdAt: Date.now() }];
          });
        }
        // Top / Bulle : fusionner s'ils arrivent quasi-simultanément
        else if (/^(Top|Bulle)\s*:/i.test(texte)) {
          setSystemToasts(prev => {
            const last = prev[prev.length - 1];
            if (last && /^(Top|Bulle)\s*:/im.test(last.text) && (Date.now() - last.createdAt) < 3000) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, text: last.text + '\n' + texte, createdAt: Date.now() };
              return updated;
            }
            return [...prev, { id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`, text: texte, type: 'result' as const, createdAt: Date.now() }];
          });
        }
        // "Résultats du tournoi" + lignes "N- pseudo" : grouper en un seul toast
        else if (/^R[ée]sultats du tournoi/i.test(texte.trim())) {
          setSystemToasts(prev => [...prev, { id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`, text: `__RESULTS__${texte.trim()}`, type: 'result' as const, createdAt: Date.now() }]);
        }
        else if (/^\d+[-–]\s*\S/.test(texte.trim())) {
          // Ligne de classement "1- Bot1" : fusionner avec le toast "Résultats" précédent
          setSystemToasts(prev => {
            const last = prev[prev.length - 1];
            if (last && last.text.startsWith('__RESULTS__') && (Date.now() - last.createdAt) < 5000) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, text: last.text + '\n' + texte.trim(), createdAt: Date.now() };
              return updated;
            }
            return prev; // Pas de toast résultat récent, ignorer
          });
        }
        // "Votre résultat" : toast spécial avec marqueur
        else if (/^Votre r[ée]sultat/i.test(texte.trim())) {
          // Essayer de fusionner avec le toast résultats s'il est récent
          setSystemToasts(prev => {
            const last = prev[prev.length - 1];
            if (last && last.text.startsWith('__RESULTS__') && (Date.now() - last.createdAt) < 5000) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, text: last.text + '\n__MYRESULT__' + texte.trim(), createdAt: Date.now() };
              return updated;
            }
            // Sinon, toast séparé
            return [...prev, { id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`, text: '__MYRESULT__' + texte.trim(), type: 'result' as const, createdAt: Date.now() }];
          });
        }
        // "Démarrage de la donne" / "prochain tournoi" → pas de toast, le timer suffit
        else if (/D[ée]marrage de la donne.*dans\s+\d+\s+seconde/i.test(texte.trim())) {
          // Timer déjà géré plus haut
        }
        else if (/prochain tournoi.*dans\s+\d+\s+minute/i.test(texte.trim())) {
          // Timer déjà géré plus haut
        }
        // Message normal
        else {
          addSystemToast(texte);
        }
      }
    });

    } // end else (legacy mode)
    
    setSocket(newSocket);
    
    return () => {
      // Éviter les sockets dupliqués (StrictMode / remount / navigation)
      if (isNeoMode) {
        newSocket.emit('neo:leave', { tableId: neoTableId });
      } else if (wantsToLeaveRef.current && connectionId) {
        newSocket.emit('game:leave', { connectionId });
      }
      newSocket.removeAllListeners();
      newSocket.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // enqueueServerMessage est stable car créé par useCallback

  // Reprise différé rétro : pas d'auto-continue au rejoin (évite d'avancer la donne en ouvrant/fermant).
  // Nouvelle donne : bots lancés après sync. Reprise : clic sur le tapis pour relancer les bots.
  useEffect(() => {
    if (!isRetroDiffereMode || !socket || retroContinueSentRef.current) return;
    if (isProcessing || queueLength > 0) return;
    if (!gameState || gameState.phase === 0) return;
    if (gameState.phase === 99999) {
      retroContinueSentRef.current = true;
      retroAwaitingManualContinueRef.current = false;
      return;
    }

    const isHumanTurn = (() => {
      switch (gameState.phase) {
        case 2:
        case 3:
          return gameState.currentPlayer === gameState.myPosition;
        case 5:
          return gameState.taker === gameState.myPosition;
        case 6:
          return gameState.currentPlayer === gameState.myPosition;
        default:
          return true;
      }
    })();

    if (isHumanTurn) {
      retroContinueSentRef.current = true;
      retroAwaitingManualContinueRef.current = false;
      return;
    }

    if (retroRejoin) {
      retroAwaitingManualContinueRef.current = true;
      setShowRetroContinueHint(true);
      return;
    }

    const timer = window.setTimeout(() => {
      if (retroContinueSentRef.current) return;
      retroContinueSentRef.current = true;
      retroAwaitingManualContinueRef.current = false;
      log.debug('[Game/Retro] Bot turn after sync — requesting continue');
      socket.emit('retro-differe:continue');
    }, 800);

    return () => window.clearTimeout(timer);
  }, [isRetroDiffereMode, socket, gameState, isProcessing, queueLength, retroRejoin]);

  const requestRetroContinue = useCallback(() => {
    if (!socket || !retroAwaitingManualContinueRef.current || retroContinueSentRef.current) return;
    retroContinueSentRef.current = true;
    retroAwaitingManualContinueRef.current = false;
    setShowRetroContinueHint(false);
    log.debug('[Game/Retro] Manual continue after rejoin');
    socket.emit('retro-differe:continue');
  }, [socket]);
  
  // Charger les emoticons disponibles (limités à ceux avec correspondance texte pour les parties Java)
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/emoticons`)
      .then(res => res.json())
      .then(data => {
        if (data.emoticons) {
          setAvailableEmoticons(data.emoticons);
        }
      })
      .catch(err => log.error('[Game] Error loading emoticons:', err));
  }, []);
  
  // Scroll automatique du chat
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [tableChatMessages]);
  
  // Fonction pour envoyer un message de chat
  const sendTableChat = useCallback(() => {
    if (!socket || !chatInput.trim()) return;
    
    const trimmedText = chatInput.trim();
    
    if (isNeoMode) {
      // Neo: server echoes chat back to all including sender
      socket.emit('neo:chat', { tableId: neoTableId, message: trimmedText });
    } else if (connectionId) {
      socket.emit('game:chat', { connectionId, texte: trimmedText });
      // Legacy: server doesn't echo own messages, add locally
      const newMessage: TableChatMessage = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        pseudo: session.pseudo,
        text: trimmedText,
        timestamp: Date.now(),
        isSystem: false,
        isOwn: true,
      };
      setTableChatMessages(prev => [...prev, newMessage]);
    }
    
    setChatInput('');
    setShowEmojiPicker(false);
  }, [socket, connectionId, chatInput, session.pseudo, isNeoMode, neoTableId]);
  
  // Gestion de l'envoi par Enter
  const handleChatKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendTableChat();
    }
  }, [sendTableChat]);
  
  // Insérer un emoticon dans l'input
  const insertEmoticon = useCallback((emoji: string) => {
    setChatInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  }, []);
  
  // Rejoindre la table ou reprendre une connexion existante
  useEffect(() => {
    if (!socket) return;
    
    // RETRO DIFFÉRÉ: join session puis start/rejoin deal
    if (isRetroDiffereMode && retroSessionId > 0) {
      log.debug('[Game/Retro] Joining session', retroSessionId, 'deal', retroDealIndex);
      socket.emit('identify', { pseudo: session.pseudo });

      const startPlaying = () => {
        if (retroRejoin) {
          socket.emit('retro-differe:rejoin', {
            sessionId: retroSessionId,
            pseudo: session.pseudo,
            dealIndex: retroDealIndex,
          });
        } else {
          socket.emit('retro-differe:start_deal', { dealIndex: retroDealIndex });
        }
      };

      const onJoined = () => {
        socket.off('retro-differe:joined', onJoined);
        socket.off('retro-differe:error', onJoinError);
        startPlaying();
      };
      const onJoinError = (data: { message?: string }) => {
        socket.off('retro-differe:joined', onJoined);
        socket.off('retro-differe:error', onJoinError);
        setError(data.message ?? 'Connexion refusée');
        setIsConnecting(false);
      };

      socket.once('retro-differe:joined', onJoined);
      socket.once('retro-differe:error', onJoinError);
      socket.emit('retro-differe:join', { sessionId: retroSessionId, pseudo: session.pseudo });
      return;
    }
    
    // NEO MODE: identify then join the neo table (or spectate)
    if (isNeoMode) {
      log.debug('[Game/Neo] Joining neo table:', neoTableId, isSpectatorMode ? '(spectator)' : '');
      socket.emit('identify', { pseudo: session.pseudo });
      if (isSpectatorMode) {
        // On attend la réponse `neo:spectator_state` avant de démarrer la lecture spectateur :
        // c'est le backend (neo:spectate) qui arbitre le temps réel vs différé selon que ce pseudo
        // est un spectateur accepté/placé à la création (cf. socket/neo.ts), pas le client.
        const onSpectatorState = (data: { realtime?: boolean }) => {
          socket.off('neo:spectator_state', onSpectatorState);
          spectatorPlayback.join({
            pseudo: session.pseudo,
            hasCompetitionAccess: session.hasCompetitionAccess,
            realtime: !!data.realtime,
          });
        };
        socket.on('neo:spectator_state', onSpectatorState);
        socket.emit('neo:spectate', { tableId: neoTableId, pseudo: session.pseudo });
        setConnectionId(neoTableId);
        setIsConnecting(false);
      } else {
        const doJoin = () => {
          socket.emit('neo:join', { tableId: neoTableId, pseudo: session.pseudo });
        };
        if (socket.connected) {
          doJoin();
        } else {
          socket.once('connect', doJoin);
        }
      }
      return;
    }
    
    // Mode table créée : on a déjà un serverConnectionId
    if (isCreatedMode && serverConnectionId) {
      log.debug('[Game] Created mode - resuming connection:', serverConnectionId);
      
      // Les infos de connexion viennent directement de gameInfo (plus de sessionStorage)
      if (gameInfo.connectionId) {
        setConnectionId(gameInfo.connectionId);
      }
      
      // Initialiser les joueurs via la queue d'actions
      if (gameInfo.table) {
        const joueursRaw = gameInfo.table.joueurs || [];
        const joueurs = joueursRaw.map((j: string | { pseudo: string; position?: number }, idx: number) => {
          const pseudo = typeof j === 'string' ? j : (j as { pseudo: string }).pseudo;
          return {
            pseudo: (pseudo || '').replace(/¶/g, ' '),
            position: idx,
            connecte: idx === 0,
          };
        });
        
        // Envoyer les infos des joueurs via la queue
        enqueueServerMessage({
          type: 'joueurs',
          vals: { joueurs },
        });
        
        // Définir ma position (le créateur est toujours position 0)
        setPosition(0);
      }
      
      // Demander au backend de commencer le polling pour cette connexion
      socket.emit('game:resume', {
        pseudo: session.pseudo,
        hashPass: session.hashPass,
        serverConnectionId,
      });
      
      setIsConnecting(false);
      return;
    }
    
    // Mode normal : rejoindre une table existante
    if (!ip || !port) return;
    
    log.debug('[Game] Attempting to join table:', { ip, port, pseudo: session.pseudo });
    log.debug('[Game] Session has hashPass:', !!session.hashPass);
    
    // Vérifier si hashPass est présent
    if (!session.hashPass) {
      log.error('[Game] hashPass missing from session - need to re-login');
      setError('Session expirée ou invalide. Veuillez vous déconnecter et vous reconnecter.');
      setIsConnecting(false);
      return;
    }
    
    // Construire les infos de la table pour la connexion
    const tableIdserv = (gameIdserv || 'libre') as GameTable['idserv'];
    const tableNbj = gameInfo.table?.nbj ?? queueGameState.playerCount ?? 4;
    const table: GameTable = {
      ip,
      port: parseInt(port, 10),
      idserv: tableIdserv,
      type: tableIdserv === 'live' ? 'live' : 'java',
      nbj: tableNbj,
      joueurs: gameInfo.table?.joueurs ?? [],
    };
    
    log.debug('[Game] Emitting game:join with table:', table);
    
    socket.emit('game:join', {
      pseudo: session.pseudo,
      hashPass: session.hashPass,
      table,
    });
  }, [socket, ip, port, isCreatedMode, serverConnectionId, session.pseudo, session.hashPass, isRetroDiffereMode, retroSessionId, retroDealIndex, retroRejoin, isNeoMode, neoTableId, gameInfo.connectionId, gameInfo.table, enqueueServerMessage, setPosition]);
  
  // NOTE: Les messages du serveur sont maintenant traités via enqueueServerMessage
  // et le système de queue d'actions (gameReducer.ts)
  
  // Actions du joueur
  const handleNeoStart = useCallback(() => {
    if (!socket || !isNeoMode) return;
    socket.emit('neo:start', { tableId: neoTableId });
  }, [socket, isNeoMode, neoTableId]);

  const handleBid = useCallback((contract: Contract) => {
    log.debug('[Game] handleBid called', { contract, connectionId, socketConnected: socket?.connected });
    if (!socket || !gameState) return;

    if (isRetroDiffereMode) {
      socket.emit('retro-differe:bid', { bid: contract });
    } else if (isNeoMode) {
      socket.emit('neo:bid', { tableId: neoTableId, bid: contract });
    } else if (connectionId) {
      socket.emit('game:bid', { connectionId, contrat: contract });
      // Le serveur Java n'envoie pas de message 'contrat' pour le joueur humain
      enqueueServerMessage({
        type: 'contrat',
        vals: {
          joueur: gameState.myPosition,
          contrat: contract,
        },
      });
    }
  }, [socket, connectionId, gameState, enqueueServerMessage, isNeoMode, isRetroDiffereMode, neoTableId]);

  const handleKingCall = useCallback((suitIndex: number) => {
    if (!socket || !gameState) return;
    const appelee = encodeKingCallAppelee(suitIndex);
    if (isNeoMode) {
      socket.emit('neo:king_call', { tableId: neoTableId, king: kingCardForSuit(suitIndex) });
    } else if (connectionId) {
      socket.emit('game:king_call', { connectionId, roi: appelee });
      enqueueServerMessage({
        type: 'appel',
        vals: { couleur: suitIndex },
      });
    }
  }, [socket, connectionId, gameState, isNeoMode, neoTableId, enqueueServerMessage]);

  const handleQueenCall = useCallback((suitIndex: number) => {
    if (!socket || !gameState) return;
    const appelee = encodeQueenCallAppelee(suitIndex);
    if (isNeoMode) {
      socket.emit('neo:king_call', { tableId: neoTableId, king: queenCardForSuit(suitIndex) });
    } else if (connectionId) {
      socket.emit('game:king_call', { connectionId, roi: appelee });
      enqueueServerMessage({
        type: 'appel',
        vals: { couleur: appelee, appelDame: 'true' },
      });
    }
  }, [socket, connectionId, gameState, isNeoMode, neoTableId, enqueueServerMessage]);

  const handleDismissKingCallReveal = useCallback(() => {
    if (!queueGameState.kingCallRevealPending) return;
    skipPendingChienAck();
    enqueueAction({ type: 'king_call_reveal_done' });
    enqueueAction({ type: 'delay', ms: DELAYS.CHIEN_SHOWN });
    enqueueAction({ type: 'chien_vu' });
  }, [queueGameState.kingCallRevealPending, enqueueAction, skipPendingChienAck]);

  const dismissKingCallRevealRef = useRef(handleDismissKingCallReveal);
  dismissKingCallRevealRef.current = handleDismissKingCallReveal;

  // Auto-fermeture de la révélation du roi appelé (défenseurs 5p)
  useEffect(() => {
    if (!queueGameState.kingCallRevealPending || !queueGameState.calledKing) return;
    const timer = setTimeout(() => dismissKingCallRevealRef.current(), DELAYS.KING_CALL_REVEAL);
    return () => clearTimeout(timer);
  }, [queueGameState.kingCallRevealPending, queueGameState.calledKing]);

  const handlePlayCard = useCallback((card: CardNumber) => {
    log.debug('[Game] handlePlayCard called', { card, connectionId, socketConnected: socket?.connected, isProcessing });
    
    if (!socket || !gameState) {
      log.warn('[Game] Cannot play: missing socket or gameState');
      return;
    }
    
    if (isProcessing) {
      log.warn('[Game] Cannot play: queue is processing, wait for animations to complete');
      return;
    }
    
    if (gameState.currentPlayer !== gameState.myPosition) {
      log.warn('[Game] Cannot play: not my turn');
      return;
    }
    
    const allowedCards = getPlayableCards(gameState.myHand, gameState.currentTrick);
    if (!allowedCards.includes(card)) {
      log.warn('[Game] Cannot play: card not allowed', { card, allowedCards });
      return;
    }
    
    // Capturer la position DOM de la carte dans la main (pour l'animation FLIP)
    if (preferences.cardPlayAnimation) {
      try {
        const cardName = getCardName(card);
        const cardEl = document.querySelector(`.player-hand-area .card[title="${CSS.escape(cardName)}"]`);
        if (cardEl) {
          const rect = cardEl.getBoundingClientRect();
          const wrapper = cardEl.closest('.hand-card-wrapper');
          let rotation = 0;
          if (wrapper) {
            const t = window.getComputedStyle(wrapper).transform;
            if (t && t !== 'none') {
              const m = t.match(/matrix\(([^)]+)\)/);
              if (m) {
                const v = m[1].split(',').map(Number);
                rotation = Math.round(Math.atan2(v[1], v[0]) * (180 / Math.PI) * 10) / 10;
              }
            }
          }
          playedCardOriginRef.current = { rect, rotation };
        }
      } catch { /* ignore */ }
    }
    
    const poigneeData = pendingPoignee && (gameState.numPli ?? 0) === 0
      ? { type: pendingPoignee.type, cartes: pendingPoignee.cards }
      : undefined;

    if (isRetroDiffereMode) {
      const neoPoignee = poigneeData
        ? { type: poigneeData.type, cards: poigneeData.cartes }
        : undefined;
      socket.emit('retro-differe:play', { card, poignee: neoPoignee });
    } else if (isNeoMode) {
      const neoPoignee = poigneeData
        ? { type: poigneeData.type, cards: poigneeData.cartes }
        : undefined;
      socket.emit('neo:play', { tableId: neoTableId, card, poignee: neoPoignee });
    } else if (connectionId) {
      socket.emit('game:play', { connectionId, carte: card, poignee: poigneeData });
      
      if (queueGameState.declaredPoignee) {
        enqueueAction({ type: 'poignee_hide' });
      }
      
      // Le serveur Java n'envoie pas de message 'carte' pour le joueur humain
      enqueueServerMessage({
        type: 'carte',
        vals: {
          joueur: gameState.myPosition,
          carte: card,
        },
      });
    }
    
    if (poigneeData) {
      setPendingPoignee(null);
    }
  }, [socket, connectionId, gameState, enqueueServerMessage, isProcessing, pendingPoignee, queueGameState.declaredPoignee, enqueueAction, isNeoMode, isRetroDiffereMode, neoTableId, preferences.cardPlayAnimation]);
  
  const handleEcart = useCallback((cards: CardNumber[]) => {
    if (!socket) return;
    if (isRetroDiffereMode) {
      socket.emit('retro-differe:ecart', { cards });
      setSelectedCards([]);
    } else if (isNeoMode) {
      socket.emit('neo:ecart', { tableId: neoTableId, cards });
      setSelectedCards([]);
    } else if (connectionId) {
      socket.emit('game:ecart', { connectionId, cartes: cards });
      setSelectedCards([]);
    }
  }, [socket, connectionId, isNeoMode, isRetroDiffereMode, neoTableId]);
  
  // Clic sur le chien pour accélérer et passer à la phase suivante
  const handleChienClick = useCallback(() => {
    log.debug('[Game] handleChienClick called, phase:', gameState?.phase);
    if (!gameState) return;
    
    // Si tout le monde a passé, la partie se termine (taker=0 est un joueur valide)
    const allPassed = gameState.taker === undefined || gameState.taker === null;
    if (allPassed) {
      log.debug('[Game] All passed, game should end');
      // TODO: gérer fin de partie quand tous passent
      return;
    }
    
    log.debug('[Game] Chien clicked, advancing to next phase');
    if (isRetroDiffereMode && socket) {
      socket.emit('retro-differe:chien_acknowledged');
    }
    skipPendingChienAck();
    enqueueAction({ type: 'chien_vu' });
    skipAnimations();
  }, [gameState, enqueueAction, skipAnimations, skipPendingChienAck, isRetroDiffereMode, socket]);
  
  // Valider l'écart
  const handleValidateEcart = useCallback(() => {
    if (!gameState) return;
    const requiredCount = ecartRequiredCount(gameState.playerCount, gameState.chien?.length);
    if (selectedCards.length !== requiredCount) return;
    
    log.debug('[Game] Validating ecart:', selectedCards);
    
    // Envoyer l'écart au serveur
    handleEcart(selectedCards);
    
    // Retirer les cartes écartées de la main et passer à la phase de jeu
    enqueueAction({ 
      type: 'ecart_done', 
      data: { cartes: selectedCards } 
    });
  }, [gameState, selectedCards, handleEcart, enqueueAction]);
  
  // Retirer une carte de l'écart (la remettre dans la main)
  const handleRemoveFromEcart = useCallback((card: CardNumber) => {
    log.debug('[Game] Removing card from ecart:', card);
    applyEcartSelection(selectedCards.filter(c => c !== card));
  }, [applyEcartSelection, selectedCards]);
  
  const handleReady = useCallback(() => {
    if (!socket || !gameState) return;
    if (isNeoMode) {
      socket.emit('neo:ready', { tableId: neoTableId });
      setIsReady(true);
    } else if (connectionId) {
      socket.emit('game:ready', { 
        connectionId,
        position: gameState.myPosition,
      });
      setIsReady(true);
    }
  }, [socket, connectionId, gameState, isNeoMode, neoTableId]);
  
  // Auto-open replay when game ends (like legacy v2 client)
  // Only if the deal was actually played (not all-passed) and we have history
  const prevPhaseForReplayRef = useRef<number | null>(null);
  useEffect(() => {
    if (!gameState) return;
    const prevPhase = prevPhaseForReplayRef.current;
    prevPhaseForReplayRef.current = gameState.phase;
    
    // Close replay when a new deal starts (phase leaves 99999)
    if (prevPhase === 99999 && gameState.phase !== 99999) {
      setShowReplay(false);
      setReplayHistory(null);
      log.debug('[Game] Closing replay mode: new deal started');
      return;
    }
    
    // Only trigger on transition TO phase 99999
    if (gameState.phase !== 99999 || prevPhase === 99999) return;
    
    // Check if this is an "all passed" deal (no real game played)
    const scores = gameState.scores;
    const allPassed = scores && scores.contrat === undefined && scores.pointsAttaque === 0 && scores.nbBouts === 0;
    if (allPassed) return;
    
    // Check we have a game history with at least one trick
    const history = getGameHistory();
    if (!history || history.tricks.length === 0) return;
    
    // Auto-open replay immediately
    setReplayHistory(history);
    setShowReplay(true);
    log.debug('[Game] Auto-opening replay mode after game end');
    
    // Si la sidebar est masquée, afficher un toast résumant le score
    if (!isSidebarVisible && scores) {
      const contratRealise = scores.contratRealise === 'true' || scores.contratRealise === true;
      const nbBouts = scores.nbBouts ?? 0;
      const pointsRequis = ({ 0: 56, 1: 51, 2: 41, 3: 36 } as Record<number, number>)[nbBouts] ?? 56;
      const pointsAttaque = scores.pointsAttaque ?? scores.pointsJoueur ?? 0;
      const contratName = scores.contrat !== undefined ? ContractNames[scores.contrat as Contract] : '';
      const result = contratRealise ? 'Le preneur gagne !' : 'La défense gagne !';
      const toastText = `${result}\n${contratName} — ${pointsAttaque} pts (requis: ${pointsRequis})`;
      addSystemToast(toastText);
    }
  }, [gameState?.phase, gameState?.scores, getGameHistory, isSidebarVisible, addSystemToast]);
  
  // ============================================
  // Vérification de poignée (pli 1, c'est à moi de jouer)
  // ============================================
  // En Tarot, chaque joueur peut déclarer une poignée au premier pli,
  // avant de jouer sa première carte, même s'il n'est pas l'entameur.
  // Il peut donc y avoir des cartes déjà posées dans le pli par d'autres joueurs.
  useEffect(() => {
    if (!gameState) return;
    if (gameState.phase !== 6) return;  // Pas en phase de jeu
    // Premier pli : numPli === 0 (0-based, pas encore de pli terminé)
    // En cas de reconnexion (différé), le serveur envoie numPli = nombre de plis terminés
    if ((gameState.numPli ?? 0) !== 0) return;
    if (gameState.currentPlayer !== gameState.myPosition) return; // Pas mon tour
    if (poigneeCheckedRef.current) return; // Déjà vérifié pour cette donne
    if (showPoigneeProposal) return; // Déjà en cours
    if (pendingPoignee) return; // Déjà validée
    
    poigneeCheckedRef.current = true;
    
    const maxType = getMaxPoigneeType(gameState.myHand, gameState.playerCount);
    if (maxType !== PoigneeType.None) {
      log.debug(`[Game] Poignée proposée: ${PoigneeNames[maxType]}, ${countTrumpsInHand(gameState.myHand)} atouts dans la main (${gameState.playerCount} joueurs)`);
      // Auto-fill avec les atouts (triés du plus haut au plus faible)
      const autoFilled = autoFillPoignee(gameState.myHand, gameState.playerCount);
      setPoigneeSelectedCards(autoFilled);
      setShowPoigneeProposal(true);
    } else {
      log.debug(`[Game] Pas assez d'atouts pour une poignée: ${countTrumpsInHand(gameState.myHand)} atouts (${gameState.playerCount} joueurs)`);
    }
  }, [gameState?.phase, gameState?.numPli, gameState?.currentPlayer, gameState?.myPosition, 
      gameState?.playerCount, gameState?.myHand, showPoigneeProposal, pendingPoignee]);
  
  // Handler : valider la poignée
  const handleValidatePoignee = useCallback(() => {
    if (!gameState) return;
    const poigneeType = getPoigneeTypeForCount(poigneeSelectedCards.length, gameState.playerCount);
    if (poigneeType === PoigneeType.None) return;
    
    // Trier les cartes du plus haut atout au plus faible pour l'envoi
    const sortedCards = sortPoigneeCards(poigneeSelectedCards);
    
    log.debug(`[Game] Poignée validée: ${PoigneeNames[poigneeType]}, cartes: ${sortedCards}`);
    setPendingPoignee({ type: poigneeType, cards: sortedCards });
    setShowPoigneeProposal(false);
    
    // Annoncer localement la poignée (affichage pour soi-même)
    // Séquence : afficher → attendre 3s → masquer automatiquement
    enqueueAction({
      type: 'poignee_announce',
      data: {
        player: gameState.myPosition,
        type: poigneeType,
        cards: sortedCards,
      },
    });
    enqueueAction({ type: 'delay', ms: DELAYS.POIGNEE_DISPLAY });
    enqueueAction({ type: 'poignee_hide' });
  }, [poigneeSelectedCards, gameState, enqueueAction]);
  
  // Handler : annuler la poignée (ne pas déclarer)
  const handleCancelPoignee = useCallback(() => {
    log.debug('[Game] Poignée annulée');
    setShowPoigneeProposal(false);
    setPoigneeSelectedCards([]);
    setPendingPoignee(null);
  }, []);
  
  // Handler : retirer une carte de la poignée
  const handleRemoveFromPoignee = useCallback((card: CardNumber) => {
    setPoigneeSelectedCards(prev => prev.filter(c => c !== card));
  }, []);
  
  // Handler : clic sur une carte de la main pendant la déclaration de poignée
  const handlePoigneeCardClick = useCallback((card: CardNumber) => {
    if (!gameState) return;
    
    // Si la carte est déjà dans la poignée, la retirer
    if (poigneeSelectedCards.includes(card)) {
      setPoigneeSelectedCards(prev => prev.filter(c => c !== card));
      return;
    }
    
    // Vérifier si la carte peut être ajoutée
    if (canAddToPoignee(card, gameState.myHand, gameState.playerCount)) {
      setPoigneeSelectedCards(prev => [...prev, card]);
    }
  }, [gameState, poigneeSelectedCards]);
  
  const handleRetroEndDeal = useCallback(() => {
    endGame();
  }, [endGame]);

  const handleLeave = useCallback(() => {
    wantsToLeaveRef.current = true;
    
    if (socket) {
      if (isNeoMode && isSpectatorMode) {
        socket.emit('neo:spectator_leave', { tableId: neoTableId });
        spectatorPlayback.leave();
      } else if (isNeoMode) {
        socket.emit('neo:leave', { tableId: neoTableId });
      } else if (connectionId) {
        socket.emit('game:leave', { connectionId });
      }
      socket.disconnect();
    }
    endGame();
  }, [socket, connectionId, endGame, isNeoMode, isSpectatorMode, neoTableId, spectatorPlayback]);
  
  // Calcul des cartes jouables
  const isMyTurn = useMemo(() => {
    if (!gameState) return false;
    if (gameState.phase !== 6) return false;
    return gameState.currentPlayer === gameState.myPosition;
  }, [gameState]);
  
  const playableCards = useMemo(() => {
    if (!gameState || gameState.phase !== 6) return [];
    // Même si ce n'est pas mon tour, calculer les cartes jouables pour info
    // Le serveur validera si le coup est légal
    return getPlayableCards(gameState.myHand, gameState.currentTrick);
  }, [gameState]);
  
  // Calcul du temps limite actif pour le décompte
  const activeTimeLimit = useMemo(() => {
    const tl = queueGameState.timeLimits;
    const turnStartedAt = queueGameState.turnStartedAt;
    if (!tl || !turnStartedAt) return null;
    
    const phase = queueGameState.phase;
    let phaseType: string | null = null;
    let seconds = 0;
    let skipInitial = false;
    
    switch (phase) {
      case 2:
        if (queueGameState.currentPlayer === queueGameState.myPosition) {
          phaseType = 'contrat';
          seconds = tl.contrat;
        }
        break;
      case 3:
        if (queueGameState.currentPlayer === queueGameState.myPosition) {
          phaseType = 'appel';
          seconds = tl.appel;
        }
        break;
      case 5:
        if (queueGameState.taker === queueGameState.myPosition) {
          phaseType = 'ecart';
          seconds = tl.ecart;
        }
        break;
      case 6:
        if (queueGameState.currentPlayer === queueGameState.myPosition) {
          phaseType = 'carte';
          seconds = tl.carte;
          // N'annoncer le début du décompte qu'au premier pli ;
          // pour les plis suivants, on garde les annonces mi-temps/urgent
          // mais pas la notification initiale de 3 secondes
          skipInitial = queueGameState.numPli > 0;
        }
        break;
      case 99999:
        // Ne pas afficher en mode live (auto-prêt) ni si le joueur a déjà cliqué "Prêt"
        if (!isReady && !isLiveMode) {
          phaseType = 'score';
          seconds = tl.score;
        }
        break;
    }
    
    if (!phaseType || seconds <= 0) return null;
    
    return { phaseType, seconds, startedAt: turnStartedAt, skipInitial };
  }, [queueGameState.timeLimits, queueGameState.turnStartedAt, queueGameState.phase,
      queueGameState.currentPlayer, queueGameState.myPosition, queueGameState.taker,
      queueGameState.numPli, isReady, isLiveMode]);

  const dealTimeLimit = useMemo(() => {
    const seconds = queueGameState.dealTimeoutSeconds;
    const startedAt = queueGameState.dealStartedAt;
    if (!seconds || !startedAt || seconds <= 0) return null;
    if (queueGameState.phase === 0 || queueGameState.phase === 99999) return null;
    return { phaseType: 'donne', seconds, startedAt };
  }, [queueGameState.dealTimeoutSeconds, queueGameState.dealStartedAt, queueGameState.phase]);
  
  // Composant de layout commun avec sidebar (nav-sidebar gérée par MainLayout)
  const GameLayout = ({ children }: { children: React.ReactNode }) => (
    <div className={gameLayoutClass}>
      {/* Colonne gauche - Chat de partie (vide pour l'instant) */}
      <aside className="game-sidebar-left">
        <div className="sidebar-header">
          <h3 className="sidebar-title">{gameSidebarTitle}</h3>
        </div>
        
        {/* Zone de chat de table */}
        <div className="game-chat-area">
          <div className="game-chat-messages">
            <div className="game-chat-placeholder">
              <span>💬 Chat de partie</span>
              <small>Les messages apparaîtront ici</small>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Zone principale */}
      {children}
    </div>
  );
  
  // Rendu selon l'état
  if (isConnecting) {
    return (
      <GameLayout>
        <div className="game-container">
          <div className="game-loading">
            <div className="loading-spinner" />
            <p>Connexion à la table...</p>
          </div>
        </div>
      </GameLayout>
    );
  }
  
  if (error) {
    return (
      <GameLayout>
        <div className="game-container">
          <div className="game-error">
            <h2>Erreur</h2>
            <p>{error}</p>
            <button onClick={handleLeave}>Retour aux tables</button>
          </div>
        </div>
      </GameLayout>
    );
  }
  
  if (!gameState) {
    const waitingSidebarContent = (
      <>
          <div className="sidebar-header">
            <div className="sidebar-header-top">
              <h3 className="sidebar-title">{gameSidebarTitle}</h3>
              <button className="quit-button" onClick={handleLeave} title="Quitter la partie">
                ✕
              </button>
            </div>
            <div className="sidebar-header-status">
              <span className="game-phase-label">
                {isLiveDealWaiting ? 'Donne en cours' : showCardCountdown ? 'Démarrage' : 'En attente'}
              </span>
              {liveDonnes && (isLiveDealWaiting || showCardCountdown) && (
                <DonneCounter
                  current={liveDonnes.current}
                  max={liveDonnes.max}
                  playersFinished={isLiveDealWaiting ? livePlayersFinished : null}
                  variant="inline"
                />
              )}
            </div>
          </div>
          
          {/* Onglets Chat / Classement (visible uniquement en mode live) */}
          {isLiveMode && (
            <div className="sidebar-tabs">
              <button
                className={`sidebar-tab ${sidebarView === 'chat' ? 'active' : ''}`}
                onClick={() => setSidebarView('chat')}
              >
                Chat
              </button>
              <button
                className={`sidebar-tab ${sidebarView === 'classement' ? 'active' : ''}`}
                onClick={() => setSidebarView('classement')}
              >
                Classement
                {liveClassement.length > 0 && (
                  <span className="tab-badge">{liveClassement.length}</span>
                )}
              </button>
            </div>
          )}
          
          {/* Contenu : Chat OU Classement */}
          {sidebarView === 'classement' && isLiveMode ? (
            <div className="game-classement-area">
              <LiveClassement
                players={liveClassement}
                sortBy={classementSortBy}
                onSortChange={setClassementSortBy}
                myPseudo={session.pseudo}
                tablePlayerCount={queueGameState.playerCount || 4}
              />
            </div>
          ) : (
            <div className="game-chat-area">
              <div className="game-chat-messages" ref={chatMessagesRef}>
                {tableChatMessages.length === 0 ? (
                  <div className="game-chat-placeholder">
                    <span>Chat de partie</span>
                    <small>Les messages apparaîtront ici</small>
                  </div>
                ) : (
                  groupChatMessages(tableChatMessages).map((item) => {
                    if (item.type === 'encadre') {
                      return (
                        <div key={item.key} className="table-chat-message system system-encadre">
                          {item.messages.map((msg) => (
                            <span key={msg.id} className="encadre-line">{msg.text}</span>
                          ))}
                        </div>
                      );
                    }
                    if (item.type === 'tournament-results') {
                      return (
                        <div key={item.key} className="table-chat-message system system-tournament">
                          {item.messages.map((msg) => {
                            const rankMatch = msg.text.trim().match(/^(\d+)\s*[-–]\s*(.+)$/);
                            if (rankMatch) {
                              const rank = parseInt(rankMatch[1], 10);
                              const pseudo = rankMatch[2].trim();
                              return (
                                <div key={msg.id} className="tournament-rank-line">
                                  <span className="tournament-rank-num">{rank <= 3 ? ['\u{1F947}','\u{1F948}','\u{1F949}'][rank-1] : `${rank}.`}</span>
                                  <PlayerAvatar pseudo={pseudo} size={16} />
                                  <span className="tournament-rank-pseudo">{pseudo}</span>
                                </div>
                              );
                            }
                            const pctMatch = msg.text.match(/(\d+)\s*%/);
                            const clMatch = msg.text.match(/classement\s*=\s*(\d+)\s*sur\s*(\d+)/i);
                            if (/^Votre résultat/i.test(msg.text.trim()) && pctMatch) {
                              const pct = parseInt(pctMatch[1], 10);
                              return (
                                <div key={msg.id} className="tournament-my-result">
                                  <span className="my-result-text">Votre résultat : <strong>{pct}%</strong>{clMatch ? ` (${clMatch[1]}/${clMatch[2]})` : ''}</span>
                                  <div className="my-result-bar"><div className="my-result-fill" style={{ width: `${pct}%` }} /></div>
                                </div>
                              );
                            }
                            return <div key={msg.id} className="tournament-title">{msg.text}</div>;
                          })}
                        </div>
                      );
                    }
                    const msg = item.messages[0];
                    return (
                      <div
                        key={item.key}
                        className={`table-chat-message ${msg.isSystem ? 'system' : msg.isOwn ? 'own' : 'other'}`}
                      >
                        {msg.isSystem ? (
                          <span className="system-text">{msg.text}</span>
                        ) : (
                          <>
                            <span className="message-author">{msg.pseudo}</span>
                            <span className="message-text">{msg.text}</span>
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              {(queueGameState.hasOtherHumans || isLiveMode) && (
                <div className="game-chat-input">
                  <div className="chat-input-wrapper">
                    <input
                      type="text"
                      placeholder="Envoyer un message..."
                      className="message-input"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={handleChatKeyPress}
                      maxLength={200}
                    />
                    <button
                      type="button"
                      className="emoji-picker-toggle"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="Emoticons"
                    >
                      😊
                    </button>
                    <button
                      type="button"
                      className="send-button"
                      onClick={sendTableChat}
                      disabled={!chatInput.trim()}
                      title="Envoyer"
                    >
                      ➤
                    </button>
                  </div>
                  {showEmojiPicker && (
                    <div className="emoji-picker-dropdown">
                      {availableEmoticons.map((emo) => (
                        <button
                          key={emo.emoji}
                          type="button"
                          className="emoji-option"
                          onClick={() => insertEmoticon(emo.emoji)}
                          title={emo.description}
                        >
                          {emo.emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
      </>
    );

    return (
      <div className={gameLayoutClass}>
        {hoistWaitingToasts && systemToasts.length > 0 && (
          <SystemToastContainer
            toasts={systemToasts}
            onDismiss={dismissToast}
          />
        )}
        <aside className="game-sidebar-left">
          {waitingSidebarContent}
        </aside>
        {showGameDrawer && (
          <GameDrawer title={gameSidebarTitle} onQuit={handleLeave}>
            <div className="game-sidebar-left game-drawer-sidebar">
              {waitingSidebarContent}
            </div>
          </GameDrawer>
        )}
        
        {/* Zone centrale - Tapis de jeu avec message d'attente */}
        <div className="game-container">
          <main className="game-table">
            {!hoistWaitingToasts && systemToasts.length > 0 && (
              <SystemToastContainer
                toasts={systemToasts}
                onDismiss={dismissToast}
              />
            )}
            {liveDonnes && !(isSidebarVisible && (isLiveDealWaiting || showCardCountdown)) && (
              <DonneCounter
                current={liveDonnes.current}
                max={liveDonnes.max}
                playersFinished={isLiveDealWaiting ? livePlayersFinished : null}
              />
            )}
            {/* Minuteur prochain tournoi */}
            {nextTournamentAt && (
              <CountdownTimer
                targetTime={nextTournamentAt}
                label="Prochain tournoi"
                expiredLabel="Le prochain tournoi va bientôt débuter…"
                approximate
              />
            )}
            {/* Minuteur prochaine donne */}
            {nextDealAt && (
              <CountdownTimer
                targetTime={nextDealAt}
                label="Prochaine donne"
                expiredLabel="La prochaine donne va bientôt débuter…"
              />
            )}
            {/* Décompte animé par cartes */}
            {showCardCountdown && (
              <CardCountdown
                deadlineAt={nextDealAt ?? undefined}
                onComplete={handleCardCountdownComplete}
              />
            )}
            {!showCardCountdown && (
            <div className="waiting-overlay">
              <p>
                {isLiveDealWaiting
                  ? 'En attente de la fin de la donne…'
                  : 'En attente du début de la partie...'}
              </p>
              {queueGameState.neoWaitingLobby && queueGameState.players.length > 0 && (
                <ul className="neo-lobby-waiting-list">
                  {queueGameState.players
                    .filter(p => !p.name.match(/^Robot/i) && !p.isConnected)
                    .map(p => (
                      <li key={p.position}>En attente de {p.name}</li>
                    ))}
                </ul>
              )}
              {isNeoMode
                && queueGameState.neoAdminPseudo
                && session.pseudo.toLowerCase() === queueGameState.neoAdminPseudo.toLowerCase() && (
                <button type="button" className="neo-start-button" onClick={handleNeoStart}>
                  Démarrer la partie
                </button>
              )}
            </div>
            )}
          </main>
        </div>
      </div>
    );
  }
  
  const scoreOnReady = isRetroDiffereMode ? handleRetroEndDeal : handleReady;
  const scoreReadyLabel = isRetroDiffereMode ? 'Retour à la saison' : undefined;
  const scoreAddToLibrary = isRetroDiffereMode
    ? undefined
    : (session.compet ? () => setShowLibraryModal(true) : undefined);

  const gameSidebarContent = (
    <>
        <div className="sidebar-header">
          <div className="sidebar-header-top">
            <h3 className="sidebar-title">{gameSidebarTitle}</h3>
            <button className="quit-button" onClick={handleLeave} title={isRetroDiffereMode ? 'Retour à la saison' : 'Quitter la partie'}>
              ✕
            </button>
          </div>
          <span className="game-phase-label">
            {gameState.phase === 1 && 'Démarrage'}
            {gameState.phase === 2 && 'Enchères'}
            {gameState.phase === 3 && 'Appel du Roi'}
            {gameState.phase === 4 && 'Le Chien'}
            {gameState.phase === 5 && 'Écart'}
            {gameState.phase === 6 && 'Jeu'}
            {gameState.phase === 99999 && 'Fin de donne'}
          </span>
        </div>
        
        {/* Onglets Chat / Classement (visible uniquement en mode live) */}
        {isLiveMode && (
          <div className="sidebar-tabs">
            <button
              className={`sidebar-tab ${sidebarView === 'chat' ? 'active' : ''}`}
              onClick={() => setSidebarView('chat')}
            >
              Chat
            </button>
            <button
              className={`sidebar-tab ${sidebarView === 'classement' ? 'active' : ''}`}
              onClick={() => setSidebarView('classement')}
            >
              Classement
              {liveClassement.length > 0 && (
                <span className="tab-badge">{liveClassement.length}</span>
              )}
            </button>
          </div>
        )}
        
        {/* Panneaux contextuels selon la phase (toujours visibles quel que soit l'onglet) */}
        <div className="game-sidebar-panels">
          {/* Suivi des enchères (tableau progressif) — visible pendant enchères, chien et écart */}
          {(gameState.phase === 2 || gameState.phase === 4 || gameState.phase === 5) && gameState.dealer !== undefined && (
            <BidTracker
              players={gameState.players}
              dealer={gameState.dealer}
              currentPlayer={gameState.currentPlayer}
              currentContract={gameState.currentContract || 0}
              taker={gameState.taker}
              playerCount={gameState.playerCount}
            />
          )}
          
          {/* Score — dans la sidebar quand elle est visible */}
          {gameState.phase === 99999 && !gameState.chien && isSidebarVisible && (
            <ScorePanel
              score={gameState.scores || {}}
              players={gameState.players}
              onReady={scoreOnReady}
              isReady={isReady}
              isLive={isLiveMode}
              takerIndex={gameState.taker}
              onAddToLibrary={scoreAddToLibrary}
              readyButtonLabel={scoreReadyLabel}
              matchFinished={isNeoMode && neoMatchFinished}
            />
          )}
        </div>
        
        {/* Contenu principal de la sidebar : Chat OU Classement */}
        {sidebarView === 'classement' && isLiveMode ? (
          <div className="game-classement-area">
            <LiveClassement
              players={liveClassement}
              sortBy={classementSortBy}
              onSortChange={setClassementSortBy}
              myPseudo={session.pseudo}
              tablePlayerCount={queueGameState.playerCount || 4}
            />
          </div>
        ) : (
          /* Zone de chat de table */
          <div className="game-chat-area">
            <div className="game-chat-messages" ref={chatMessagesRef}>
              {tableChatMessages.length === 0 ? (
                <div className="game-chat-placeholder">
                  <span>Chat de partie</span>
                  <small>Les messages apparaîtront ici</small>
                </div>
              ) : (
                groupChatMessages(tableChatMessages).map((item) => {
                  if (item.type === 'encadre') {
                    return (
                      <div key={item.key} className="table-chat-message system system-encadre">
                        {item.messages.map((msg) => (
                          <span key={msg.id} className="encadre-line">{msg.text}</span>
                        ))}
                      </div>
                    );
                  }
                  if (item.type === 'tournament-results') {
                    return (
                      <div key={item.key} className="table-chat-message system system-tournament">
                        {item.messages.map((msg) => {
                          const rankMatch = msg.text.trim().match(/^(\d+)\s*[-–]\s*(.+)$/);
                          if (rankMatch) {
                            const rank = parseInt(rankMatch[1], 10);
                            const pseudo = rankMatch[2].trim();
                            return (
                              <div key={msg.id} className="tournament-rank-line">
                                <span className="tournament-rank-num">{rank <= 3 ? ['\u{1F947}','\u{1F948}','\u{1F949}'][rank-1] : `${rank}.`}</span>
                                <PlayerAvatar pseudo={pseudo} size={16} />
                                <span className="tournament-rank-pseudo">{pseudo}</span>
                              </div>
                            );
                          }
                          const pctMatch = msg.text.match(/(\d+)\s*%/);
                          const clMatch = msg.text.match(/classement\s*=\s*(\d+)\s*sur\s*(\d+)/i);
                          if (/^Votre résultat/i.test(msg.text.trim()) && pctMatch) {
                            const pct = parseInt(pctMatch[1], 10);
                            return (
                              <div key={msg.id} className="tournament-my-result">
                                <span className="my-result-text">Votre résultat : <strong>{pct}%</strong>{clMatch ? ` (${clMatch[1]}/${clMatch[2]})` : ''}</span>
                                <div className="my-result-bar"><div className="my-result-fill" style={{ width: `${pct}%` }} /></div>
                              </div>
                            );
                          }
                          return <div key={msg.id} className="tournament-title">{msg.text}</div>;
                        })}
                      </div>
                    );
                  }
                  const msg = item.messages[0];
                  return (
                    <div
                      key={item.key}
                      className={`table-chat-message ${msg.isSystem ? 'system' : msg.isOwn ? 'own' : 'other'}`}
                    >
                      {msg.isSystem ? (
                        <span className="system-text">{msg.text}</span>
                      ) : (
                        <>
                          {!msg.isOwn && <span className="message-author">{msg.pseudo}</span>}
                          <span className="message-text">{msg.text}</span>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {/* Zone de saisie - masquée si seul humain à la table (sauf en mode live) */}
            {(queueGameState.hasOtherHumans || isLiveMode) && (
              <div className="game-chat-input">
                <div className="chat-input-wrapper">
                  <input 
                    type="text" 
                    placeholder="Envoyer un message..." 
                    className="message-input"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={handleChatKeyPress}
                    maxLength={200}
                  />
                  <button
                    type="button"
                    className="emoji-picker-toggle"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    title="Emoticons"
                  >
                    😊
                  </button>
                  <button
                    type="button"
                    className="send-button"
                    onClick={sendTableChat}
                    disabled={!chatInput.trim()}
                    title="Envoyer"
                  >
                    ➤
                  </button>
                </div>
                {showEmojiPicker && (
                  <div className="emoji-picker-dropdown">
                    {availableEmoticons.map((emo) => (
                      <button
                        key={emo.emoji}
                        type="button"
                        className="emoji-option"
                        onClick={() => insertEmoticon(emo.emoji)}
                        title={emo.description}
                      >
                        {emo.emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
    </>
  );

  return (
    <div className={gameLayoutClass}>
      {isSpectatorMode && (
        <div className="game-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1rem' }}>
          <div className="live-spectator-header">
            <h2>Spectateur — {gameInfo.table?.comm ?? neoTableId.substring(0, 8)}</h2>
            <button type="button" onClick={handleLeave}>Quitter</button>
          </div>
          <TimedReplayView
            events={spectatorPlayback.events}
            chatMessages={spectatorPlayback.chatMessages}
            players={spectatorPlayback.playbackState?.players ?? []}
            playerCount={4}
            waitingMessage={spectatorPlayback.playbackState?.message ?? (isConnecting ? 'Connexion…' : undefined)}
          />
          {spectatorPlayback.error && <p className="spectator-waiting">{spectatorPlayback.error}</p>}
        </div>
      )}

      {!isSpectatorMode && (
      <>
      {/* Sidebar : colonne à gauche en paysage, dock en haut en portrait */}
      <aside className="game-sidebar-left">
        {gameSidebarContent}
      </aside>

      {/* Drawer : téléphone paysage uniquement (pas de place pour le dock) */}
      {showGameDrawer && (
        <GameDrawer title={gameSidebarTitle} onQuit={handleLeave}>
          <div className="game-sidebar-left game-drawer-sidebar">
            {gameSidebarContent}
          </div>
        </GameDrawer>
      )}
      
      <div className={`game-container table-bg-${effectiveTableBase}`}>
      {spectatorLiveWarning && (
        <div className="spectator-live-warning">{spectatorLiveWarning}</div>
      )}
      <TableBackgroundLayer
        base={effectiveTableBase}
        overlay={preferences.tableOverlay}
        emblem={effectiveTableEmblem}
        isDarkMode={resolvedTheme === 'dark'}
        duplicateStyle={useDupTable ? preferences.duplicateTableStyle : undefined}
        trickEvents={bgTrickEvents}
        chienEvent={bgChienEvent}
        gameEnded={gameState?.phase === 99999}
        partnerPileMerge={
          gameState &&
          gameState.playerCount === 5 &&
          gameState.partnerRevealed &&
          gameState.partner !== undefined &&
          gameState.taker !== undefined &&
          gameState.partner !== gameState.taker
            ? {
                partnerScreenPos: toScreenPosition(
                  gameState.partner,
                  gameState.myPosition,
                  gameState.playerCount,
                ),
                takerScreenPos: toScreenPosition(
                  gameState.taker,
                  gameState.myPosition,
                  gameState.playerCount,
                ),
              }
            : undefined
        }
      />
      {/* Table de jeu */}
      <main 
        className={`game-table ${isProcessing || queueLength > 0 ? 'animating' : ''}${showRetroContinueHint ? ' retro-awaiting-continue' : ''}`}
        onClick={() => {
          if (retroAwaitingManualContinueRef.current) {
            requestRetroContinue();
            return;
          }
          if (isProcessing || queueLength > 0) {
            skipAnimations();
          }
        }}
        style={{ cursor: (showRetroContinueHint || isProcessing || queueLength > 0) ? 'pointer' : 'default' }}
      >
        {showRetroContinueHint && (
          <div className="retro-continue-hint">Cliquez sur le tapis pour reprendre</div>
        )}
        {/* Replay Mode (rendered inside game-table) */}
        {showReplay && replayHistory && (
          <ReplayMode
            history={replayHistory}
            onReady={!isSidebarVisible && gameState.phase === 99999 && !isLiveMode ? scoreOnReady : undefined}
            isReady={isReady}
            isLive={isLiveMode}
            scorePopup={!isSidebarVisible && gameState.phase === 99999 && !gameState.chien ? (
              <ScorePanel
                score={gameState.scores || {}}
                players={gameState.players}
                onReady={scoreOnReady}
                isReady={isReady}
                isLive={isLiveMode}
                takerIndex={gameState.taker}
                hideReadyButton
                readyButtonLabel={scoreReadyLabel}
                matchFinished={isNeoMode && neoMatchFinished}
              />
            ) : undefined}
          />
        )}
        
        {/* Toasts système (centré sur le tapis de jeu) */}
        {systemToasts.length > 0 && (
          <SystemToastContainer
            toasts={systemToasts}
            onDismiss={dismissToast}
          />
        )}
        {/* Compteur de donne Live (coin haut gauche) */}
        {liveDonnes && (
          <DonneCounter
            current={liveDonnes.current}
            max={liveDonnes.max}
          />
        )}
        {/* Minuteur prochain tournoi */}
        {nextTournamentAt && (
          <CountdownTimer
            targetTime={nextTournamentAt}
            label="Prochain tournoi"
            expiredLabel="Le prochain tournoi va bientôt débuter…"
            approximate
          />
        )}
        {/* Minuteur prochaine donne */}
        {nextDealAt && (
          <CountdownTimer
            targetTime={nextDealAt}
            label="Prochaine donne"
            expiredLabel="La prochaine donne va bientôt débuter…"
          />
        )}
        {/* Décompte animé par cartes */}
        {showCardCountdown && (
          <CardCountdown
            deadlineAt={nextDealAt ?? undefined}
            onComplete={handleCardCountdownComplete}
          />
        )}
        
        {/* Indicateur d'animation en cours */}
        {(isProcessing || queueLength > 0) && (
          <div className="animation-skip-hint">
            Cliquez pour accélérer...
          </div>
        )}
        
        {/* Décompte de temps limite (donne Live + action en cours) */}
        {(dealTimeLimit || activeTimeLimit) && (
          <div className="timeout-stack">
            {dealTimeLimit && (
              <TimeoutCountdown
                key={`donne-${dealTimeLimit.startedAt}`}
                totalSeconds={dealTimeLimit.seconds}
                startedAt={dealTimeLimit.startedAt}
                phaseType="donne"
              />
            )}
            {activeTimeLimit && (
              <TimeoutCountdown
                key={`${activeTimeLimit.phaseType}-${activeTimeLimit.startedAt}`}
                totalSeconds={activeTimeLimit.seconds}
                startedAt={activeTimeLimit.startedAt}
                phaseType={activeTimeLimit.phaseType}
                skipInitialNotification={activeTimeLimit.skipInitial}
              />
            )}
          </div>
        )}
        
        {/* Lobby Neo : en attente des autres joueurs ou du démarrage */}
        {isNeoMode && queueGameState.neoWaitingLobby && gameState.myHand.length === 0 && (
          <div className="waiting-overlay">
            <p>En attente du début de la partie...</p>
            {queueGameState.players
              .filter(p => !/^Robot/i.test(p.name) && !p.isConnected)
              .map(p => (
                <p key={p.position} className="neo-lobby-waiting-player">En attente de {p.name}</p>
              ))}
            {queueGameState.neoAdminPseudo
              && session.pseudo.toLowerCase() === queueGameState.neoAdminPseudo.toLowerCase() && (
              <button type="button" className="neo-start-button" onClick={handleNeoStart}>
                Démarrer la partie
              </button>
            )}
          </div>
        )}

        {/* Panneau d'enchères - centré sur le tapis */}
        {gameState.phase === 2 && (() => {
          // Ne pas afficher le panneau si tout le monde a passé
          const dealerBid = gameState.dealer !== undefined ? gameState.players[gameState.dealer]?.bid : undefined;
          if (dealerBid !== undefined && (!gameState.currentContract || Number(gameState.currentContract) === 0)) {
            return null;
          }
          // Ne pas afficher le panneau si le joueur courant a déjà enchéri
          const currentPlayerBid = gameState.players[gameState.currentPlayer]?.bid;
          if (currentPlayerBid !== undefined) {
            return null;
          }
          const myTurn = gameState.currentPlayer === gameState.myPosition;
          const currentBidder = gameState.players[gameState.currentPlayer];
          log.debug('[Game] Bidding state:', { 
            currentPlayer: gameState.currentPlayer, 
            myPosition: gameState.myPosition,
            myTurn,
            currentBidder: currentBidder?.pseudo,
            players: gameState.players.map(p => ({ pseudo: p.pseudo, bid: p.bid })),
          });
          return (
            <BiddingPanel
              currentContract={gameState.currentContract || 0}
              onBid={handleBid}
              myTurn={myTurn}
              currentBidderName={currentBidder?.pseudo}
              bidConstraints={queueGameState.bidConstraints}
            />
          );
        })()}

        {/* Appel du roi — 5 joueurs */}
        {gameState.phase === 3 && gameState.playerCount === 5 && !queueGameState.kingCallRevealPending && (
          <KingCallPanel
            myTurn={gameState.taker === gameState.myPosition}
            canCallQueen={hasAllKingsInHand(gameState.myHand)}
            onCallKing={handleKingCall}
            onCallQueen={handleQueenCall}
          />
        )}

        {/* Zone des cartes posées (pli en cours) - toujours rendu pour calculer la position de la main */}
        <TrickDisplay 
          cards={gameState.phase === 6 ? gameState.currentTrick : []}
          playerPositions={gameState.phase === 6 ? (gameState.trickPlayerPositions || []) : []}
          playerCount={gameState.playerCount}
          myPosition={gameState.myPosition}
          players={gameState.players}
          currentContract={gameState.currentContract}
          takerPosition={gameState.taker}
          partnerPosition={gameState.partner}
          partnerRevealed={gameState.partnerRevealed}
          currentPlayer={gameState.currentPlayer}
          phase={gameState.phase}
          handCount={gameState.myHand.length}
          handLayout={preferences.handLayout}
          animateCardPlay={preferences.cardPlayAnimation}
          trickCardRotation={preferences.trickCardRotation}
          getLocalCardOrigin={getLocalCardOrigin}
          isLive={isLiveMode}
        />

        {/* Révélation du roi appelé (défenseurs, 5 joueurs) — avant le chien */}
        {queueGameState.kingCallRevealPending &&
          queueGameState.calledKing &&
          gameState.playerCount === 5 &&
          gameState.myPosition !== gameState.taker && (
          <CalledKingReveal
            calledKing={queueGameState.calledKing}
            onDismiss={handleDismissKingCallReveal}
          />
        )}
        
        {/* Chien visible - Phase 4 (pas pendant la révélation du roi) */}
        {gameState.chien && gameState.phase === 4 && !queueGameState.kingCallRevealPending && queueGameState.awaitingChienDismiss && (
          <div className="table-center">
            {(() => {
              // Tout le monde a passé si taker est undefined/null (pas 0 qui est un joueur valide)
              const allPassed = gameState.taker === undefined || gameState.taker === null;
              log.debug('[Game] Chien display:', { taker: gameState.taker, allPassed, contract: gameState.currentContract });
              return (
                <ChienView 
                  chien={gameState.chien}
                  allPassed={allPassed}
                  onClick={handleChienClick}
                />
              );
            })()}
          </div>
        )}
        
        {/* Sièges des joueurs - masqués pendant le démarrage, les enchères, le chien, l'écart, le jeu et la fin de donne */}
        {/* (TrickDisplay gère déjà l'affichage des cartouches joueurs pendant ces phases) */}
        {gameState.phase !== 1 && gameState.phase !== 2 && gameState.phase !== 3 && gameState.phase !== 4 && gameState.phase !== 5 && gameState.phase !== 6 && gameState.phase !== 99999 && (
          <div className={`player-seats seats-${gameState.playerCount}`}>
            {gameState.players.map((player, index) => {
              // Calculer la position relative au joueur actuel
              const relativePos = (index - gameState.myPosition + gameState.playerCount) % gameState.playerCount;
              // Mapping des positions : le joueur suivant (pos+1) est à l'Est (droite)
              // S=0 (bottom), E=1 (right), N=2 (top), O=3 (left)
              const positions: Record<number, Record<number, 'bottom' | 'left' | 'top' | 'right' | 'top-left' | 'top-right'>> = {
                3: { 0: 'bottom', 1: 'right', 2: 'left' },
                4: { 0: 'bottom', 1: 'right', 2: 'top', 3: 'left' },
                5: { 0: 'bottom', 1: 'right', 2: 'top-right', 3: 'top-left', 4: 'left' },
              };
              const position = positions[gameState.playerCount]?.[relativePos] || 'bottom';
              
              const isBiddingPhase = gameState.phase === 2;
              // Le pli est "en fin" quand toutes les cartes sont posées mais pas encore ramassées
              const isTrickEnding = gameState.currentTrick.length >= gameState.playerCount;
              
              return (
                <PlayerSeat
                  key={player.pseudo || index}
                  player={player}
                  position={position}
                  isCurrentPlayer={gameState.currentPlayer === index}
                  isTaker={gameState.taker === index}
                  takerContract={gameState.currentContract}
                  isBidding={isBiddingPhase}
                  isCurrentBidder={isBiddingPhase && gameState.currentPlayer === index}
                  isTrickEnding={isTrickEnding}
                  isPartnerRevealed={
                    gameState.partnerRevealed &&
                    gameState.partner === index
                  }
                  isSelfCalled={
                    gameState.partnerRevealed &&
                    gameState.partner === gameState.taker &&
                    index === gameState.taker
                  }
                  cardCount={index !== gameState.myPosition ? gameState.myHand.length : undefined}
                  tricksWon={player.tricksWon || 0}
                />
              );
            })}
          </div>
        )}
        
        {/* Panneau d'écart - Phase 5 */}
        {gameState.phase === 5 && gameState.taker === gameState.myPosition && (
          <EcartPanel
            selectedCards={selectedCards}
            requiredCount={ecartRequiredCount(gameState.playerCount, gameState.chien?.length)}
            onValidate={handleValidateEcart}
            onRemoveCard={handleRemoveFromEcart}
            onClearAll={() => setSelectedCards([])}
          />
        )}
        
        {/* Panneau de déclaration de poignée - Phase 6, premier pli, mon tour */}
        {showPoigneeProposal && gameState.phase === 6 && (
          <PoigneePanel
            selectedCards={poigneeSelectedCards}
            maxType={getMaxPoigneeType(gameState.myHand, gameState.playerCount)}
            playerCount={gameState.playerCount}
            onValidate={handleValidatePoignee}
            onCancel={handleCancelPoignee}
            onRemoveCard={handleRemoveFromPoignee}
          />
        )}
        
        {/* Badge de poignée validée (en attente d'envoi avec la carte) */}
        {pendingPoignee && !showPoigneeProposal && gameState.phase === 6 && (
          <div className="poignee-badge">
            {PoigneeNames[pendingPoignee.type]} déclarée — jouez votre carte
          </div>
        )}
        
        {/* Affichage d'une poignée déclarée (par un autre joueur ou en replay) */}
        {queueGameState.declaredPoignee && !showPoigneeProposal && (
          <PoigneeDisplay
            declaredPoignee={queueGameState.declaredPoignee}
            playerName={gameState.players[queueGameState.declaredPoignee.player]?.pseudo || `Joueur ${queueGameState.declaredPoignee.player}`}
          />
        )}
        
        {/* Score en overlay sur le tapis — uniquement si sidebar masquée ET replay pas visible */}
        {gameState.phase === 99999 && !gameState.chien && !isSidebarVisible && !showReplay && (
          <div className="score-overlay">
            <ScorePanel
              score={gameState.scores || {}}
              players={gameState.players}
              onReady={scoreOnReady}
              isReady={isReady}
              isLive={isLiveMode}
              takerIndex={gameState.taker}
              readyButtonLabel={scoreReadyLabel}
              matchFinished={isNeoMode && neoMatchFinished}
            />
          </div>
        )}
        
        {/* Main du joueur */}
        <div className="player-hand-area">
          <PlayerHand
            key={`${gameState.idPartie || 'initial'}-${preferences.handLayout}`}
            cards={gameState.myHand}
            playableCards={
              showPoigneeProposal
                ? gameState.myHand.filter(c => canAddToPoignee(c, gameState.myHand, gameState.playerCount))  // Cartes ajoutables à la poignée
                : gameState.phase === 5 
                  ? gameState.myHand.filter(c => canDiscard(c))  // Cartes écartables en phase 5
                  : playableCards
            }
            selectedCards={showPoigneeProposal ? poigneeSelectedCards : selectedCards}
            onCardClick={
              showPoigneeProposal
                ? handlePoigneeCardClick  // Mode poignée: sélectionner/désélectionner les atouts
                : gameState.phase === 6 && isMyTurn && !isProcessing
                  ? handlePlayCard 
                  : undefined
            }
            onSelectionChange={
              // multiSelect + onSelectionChange = mode écart (sélection gérée internalement par PlayerHand)
              // En mode poignée, on NE passe PAS onSelectionChange car la sélection est gérée
              // externalement via onCardClick + selectedCards (multiSelect=false)
              gameState.phase === 5 && gameState.taker === gameState.myPosition 
                ? applyEcartSelection 
                : undefined
            }
            multiSelect={
              // multiSelect uniquement pour l'écart (sélection interne PlayerHand)
              // Pour la poignée, on utilise le mode clic simple avec selectedCards externe
              !showPoigneeProposal && gameState.phase === 5 && gameState.taker === gameState.myPosition
            }
            maxSelection={ecartRequiredCount(gameState.playerCount, gameState.chien?.length)}
            cardSize="large"
            animate={true}
            disabled={
              showPoigneeProposal 
                ? false  // En mode poignée, la main est active
                : (gameState.phase === 6 && (!isMyTurn || isProcessing))
            }
            handLayout={preferences.handLayout}
          />
        </div>
      </main>
      
      </div>{/* fin game-container */}

      {/* Library modal */}
      {showLibraryModal && replayHistory && (
        <AddToLibraryModal
          pseudo={session.pseudo}
          history={replayHistory}
          originType={
            (gameInfo.idserv === 'diff_indiv' ? 'differe_indiv'
              : gameInfo.idserv === 'diff_tq' ? 'differe_tq'
              : gameInfo.idserv === 'defis' ? 'defi'
              : gameInfo.idserv === 'libre' ? 'libre'
              : 'libre') as LibraryOriginType
          }
          originId={gameInfo.neoTableId ?? undefined}
          myPosition={gameState?.myPosition ?? 0}
          onClose={() => setShowLibraryModal(false)}
        />
      )}
      </>
      )}
    </div>
  );
}
